// Extracted from CaseBuddy.vue in refactor pass 2.
// Project-file persistence, formula bar, context menu, address color formatting, spreadsheet toolbar, and output-sheet utility methods.
// These are Options API method definitions and intentionally use `this`.

import {
  abbreviateCaseBuddyHash,
  buildCaseAddressColorCoverageAnalysis,
  buildCaseAddressColorState,
  buildPathOperationAnalysis,
  buildSaplingPreparationAnalysis,
  buildTopLevelMainEntryDescriptors,
  CASE_ADDRESS_COLOR_CLEAR_TOOL_RUN_ID,
  CASE_ADDRESS_COLOR_TOOL_RUN_ID,
  clearCaseAddressColorState,
  createBlankWorkbookState,
  createCaseSheetState,
  createCaseWorkbookState,
  createDefaultCaseSupportState,
  createEmptyCaseSummary,
  createProjectFileId,
  findExactPathOperationCandidateForRow,
  CASE_COLUMN_HEADERS,
  LEGACY_INPUT_WORKBOOK_KEY,
  LEGACY_SINGLE_SHEET_KEY,
  PROJECT_FILES_INDEX_KEY,
  normalizeCaseMatrix,
  normalizeCell,
  parseCaseEntries,
  rowHasMeaningfulData,
  WORKFLOW_SIGNAL_ADDRESS_FLOW_COLORS_APPLIED
} from '../CaseBuddyCore.js';
import {
  addMockData as loadMockData,
  analyzeColumnTypes as detectColumnTypes,
  applyBackgroundColor as applyBackgroundColorToSelection,
  applyBorderStyle as applyBorderStyleToSelection,
  applyTextColor as applyTextColorToSelection,
  clearOutput as clearSheetOutput,
  clearSearch as clearSheetSearch,
  exportToCSV as exportSheetToCSV,
  fillDownSelection as fillDownPattern,
  highlightAlternatingRows as zebraStripeRows,
  highlightColumn2 as highlightErrorColumn,
  highlightKeywordRows as highlightKeywordRowsInSheet,
  processData as processSpreadsheetData,
  restoreSavedSpreadsheet as restoreSpreadsheetState,
  saveSpreadsheet as persistSpreadsheetState,
  setAlignment as setSelectionAlignment,
  setFontSize as setSelectionFontSize,
  setVerticalAlignment as setSelectionVerticalAlignment,
  toggleCellStyle as toggleSelectionCellStyle,
  toggleWrapText as toggleSelectionWrap
} from '../HermesSpreadsheetUtils';

const CASE_FILE_BROWSER_DEFAULT_ROWS = Object.freeze([]);

function caseFileBrowserNormalizeSheetName(name = '') {
  return String(name || '').trim().toLowerCase().replace(/[\s_/-]+/g, '');
}

function caseFileBrowserIsLogicalSheetName(name = '', role = 'main') {
  const normalized = caseFileBrowserNormalizeSheetName(name);
  if (role === 'main') return ['maindata', 'main', 'inputdata', 'input', 'caseinput', 'sheet1'].includes(normalized);
  if (role === 'raw') return ['rawdata', 'raw', 'sourcedata', 'source'].includes(normalized);
  return false;
}

function caseFileBrowserFirstMeaningfulRow(rows = []) {
  return (Array.isArray(rows) ? rows : []).find(row => Array.isArray(row) && rowHasMeaningfulData(row)) || [];
}


function caseFileBrowserParseTimestamp(value = '') {
  const text = normalizeCell(value);
  if (!text) return null;
  const normalized = text.replace(/\s+UTC$/i, 'Z').replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2}(?::\d{2})?)Z?$/i, '$1T$2Z');
  const parsed = Date.parse(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function caseFileBrowserFormatShortDate(timestamp = null) {
  if (!Number.isFinite(Number(timestamp))) return '';
  try {
    return new Date(Number(timestamp)).toLocaleDateString();
  } catch {
    return '';
  }
}

function caseFileBrowserCountMapValues(map = new Map(), limit = 6) {
  return Array.from(map.entries())
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));
}

function caseFileBrowserTokenIsBtc(row = []) {
  const token = normalizeCell(row?.[6]).toUpperCase();
  const chainId = normalizeCell(row?.[19]).toLowerCase();
  const chainName = normalizeCell(row?.[20]).toLowerCase();
  return token === 'BTC' || chainId === 'btc' || chainId === '0' || chainName === 'btc' || chainName.includes('bitcoin');
}

function caseFileBrowserTokenIsEvmLike(row = []) {
  const token = normalizeCell(row?.[6]).toUpperCase();
  const chainId = normalizeCell(row?.[19]).toLowerCase();
  const chainName = normalizeCell(row?.[20]).toLowerCase();
  return token === 'ETH' || chainId === '1' || chainName.includes('ethereum');
}

function caseFileBrowserNormalizeAddressKey(value = '') {
  return normalizeCell(value)
    .replace(/\u00a0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/^[`'"]+|[`'"]+$/g, '')
    .trim()
    .toLowerCase();
}

function caseFileBrowserEntryRows(entry = {}) {
  return (Array.isArray(entry?.rows) ? entry.rows : [])
    .map(item => Array.isArray(item) ? item : Array.isArray(item?.row) ? item.row : [])
    .filter(row => Array.isArray(row) && rowHasMeaningfulData(row));
}

function caseFileBrowserEntrySystemNotes(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map(row => normalizeCell(row?.[9]))
    .filter(Boolean)
    .join(' ');
}

function caseFileBrowserSplitAddressCell(value = '') {
  return String(value ?? '')
    .split(/[\n,;]+/)
    .map(part => caseFileBrowserNormalizeAddressKey(part))
    .filter(Boolean);
}

function caseFileBrowserEntryHasSingleSelfTransfer(rows = []) {
  const fromKeys = new Set();
  const toKeys = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    caseFileBrowserSplitAddressCell(row?.[2]).forEach(address => fromKeys.add(address));
    caseFileBrowserSplitAddressCell(row?.[3]).forEach(address => toKeys.add(address));
  });
  if (fromKeys.size !== 1 || toKeys.size !== 1) return false;
  const [fromKey = ''] = Array.from(fromKeys);
  const [toKey = ''] = Array.from(toKeys);
  return !!fromKey && fromKey === toKey;
}

function caseFileBrowserEntryExampleLabel(entry = {}, rows = []) {
  const first = rows[0] || [];
  const label = normalizeCell(first?.[0]);
  const hash = normalizeCell(entry?.hash || first?.[1]);
  return [label, hash ? abbreviateCaseBuddyHash(hash) : ''].filter(Boolean).join(' · ');
}

function caseFileBrowserCollectSwapBridgeCounts(entries = []) {
  let swapEntryCount = 0;
  let bridgeEntryCount = 0;
  let swapBridgeSelfTransferCount = 0;
  const swapExamples = [];
  const bridgeExamples = [];

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const rows = caseFileBrowserEntryRows(entry);
    if (rows.length === 0) return;
    const notes = caseFileBrowserEntrySystemNotes(rows);
    if (!notes) return;
    const hasSingleSelfTransfer = caseFileBrowserEntryHasSingleSelfTransfer(rows);
    const hasSwapSignal = /\bswapped\b/i.test(notes) && hasSingleSelfTransfer;
    const hasBridgeSignal = /\bbridged\b/i.test(notes);
    const example = caseFileBrowserEntryExampleLabel(entry, rows);

    if (hasSwapSignal) {
      swapEntryCount += 1;
      swapBridgeSelfTransferCount += 1;
      if (example && swapExamples.length < 4) swapExamples.push(example);
    }

    if (hasBridgeSignal) {
      bridgeEntryCount += 1;
      if (hasSingleSelfTransfer && !hasSwapSignal) swapBridgeSelfTransferCount += 1;
      if (example && bridgeExamples.length < 4) bridgeExamples.push(example);
    }
  });

  const swapBridgeSummary = [
    swapEntryCount > 0 ? `${swapEntryCount} swap${swapEntryCount === 1 ? '' : 's'}` : '',
    bridgeEntryCount > 0 ? `${bridgeEntryCount} bridge${bridgeEntryCount === 1 ? '' : 's'}` : ''
  ].filter(Boolean).join(' · ');

  return {
    swapCount: swapEntryCount,
    bridgeCount: bridgeEntryCount,
    swapBridgeCount: swapEntryCount + bridgeEntryCount,
    swapEntryCount,
    bridgeEntryCount,
    swapBridgeSelfTransferCount,
    swapExamples,
    bridgeExamples,
    swapBridgeSummary
  };
}

function caseFileBrowserMakeDateRange(rows = []) {
  const timestamps = (Array.isArray(rows) ? rows : [])
    .map(row => caseFileBrowserParseTimestamp(row?.[4]))
    .filter(value => Number.isFinite(value));
  if (timestamps.length === 0) return { start: '', end: '', label: 'No dated rows' };
  const start = Math.min(...timestamps);
  const end = Math.max(...timestamps);
  const startLabel = caseFileBrowserFormatShortDate(start);
  const endLabel = caseFileBrowserFormatShortDate(end);
  return {
    start: startLabel,
    end: endLabel,
    label: startLabel && endLabel && startLabel !== endLabel ? `${startLabel} → ${endLabel}` : (startLabel || endLabel || 'Dated')
  };
}

function caseFileBrowserCollectCounts(rows = [], topMainRows = [], entries = []) {
  const chainCounts = new Map();
  const tokenCounts = new Map();
  const addresses = new Set();
  let btcMainTransactionCount = 0;
  let ethMainTransactionCount = 0;
  let pathEndingRows = 0;
  let qualifiedOutputRows = 0;
  let fundsFollowedRows = 0;
  let missingPathLabels = 0;

  (Array.isArray(rows) ? rows : []).slice(1).forEach((row) => {
    if (!Array.isArray(row) || !rowHasMeaningfulData(row)) return;
    const chain = normalizeCell(row?.[20]) || normalizeCell(row?.[19]);
    const token = normalizeCell(row?.[6]).toUpperCase();
    const fromAddress = normalizeCell(row?.[2]);
    const toAddress = normalizeCell(row?.[3]);
    if (chain) chainCounts.set(chain, (chainCounts.get(chain) || 0) + 1);
    if (token) tokenCounts.set(token, (tokenCounts.get(token) || 0) + 1);
    if (fromAddress) addresses.add(fromAddress.toLowerCase());
    if (toAddress) addresses.add(toAddress.toLowerCase());
    if (normalizeCell(row?.[1]) && !normalizeCell(row?.[0])) missingPathLabels += 1;
    if (/\bpath\s+ends\b/i.test(normalizeCell(row?.[16]))) pathEndingRows += 1;
    if (normalizeCell(row?.[12])) fundsFollowedRows += 1;
    if (normalizeCell(row?.[12]) && (normalizeCell(row?.[17]) || normalizeCell(row?.[18]))) qualifiedOutputRows += 1;
  });

  (Array.isArray(topMainRows) ? topMainRows : []).forEach((descriptor) => {
    const row = descriptor?.entryRows?.[0]?.row || [];
    if (caseFileBrowserTokenIsBtc(row)) btcMainTransactionCount += 1;
    if (caseFileBrowserTokenIsEvmLike(row)) ethMainTransactionCount += 1;
  });

  return {
    chainCounts,
    tokenCounts,
    chainNames: caseFileBrowserCountMapValues(chainCounts),
    tokenSymbols: caseFileBrowserCountMapValues(tokenCounts),
    addressCount: addresses.size,
    btcMainTransactionCount,
    ethMainTransactionCount,
    pathEndingRows,
    qualifiedOutputRows,
    fundsFollowedRows,
    missingPathLabels,
    ...caseFileBrowserCollectSwapBridgeCounts(entries)
  };
}


const CASE_PREP_TO_CASE_BUDDY_HANDOFF_STORAGE_KEY = 'pt:casePrepCaseBuddyHandoff';
const CASE_PREP_TO_CASE_BUDDY_HANDOFF_SCHEMA = 'case_prep_to_case_buddy_v1';

function normalizeCasePrepBuddyHandoffText(value = '') {
  return String(value ?? '').replace(/\u00a0/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
}

function normalizeCasePrepBuddyHandoffKey(value = '') {
  return normalizeCasePrepBuddyHandoffText(value).toLowerCase();
}

function isLikelyCasePrepBuddyHandoffHeader(value = '') {
  const key = normalizeCasePrepBuddyHandoffKey(value).replace(/[\s_/-]+/g, '');
  return ['transactionhash', 'txhash', 'hash', 'toaddress', 'to', 'destinationaddress'].includes(key);
}

function normalizeCasePrepBuddyHandoffTransaction(rawTransaction = {}, index = 0) {
  const txHash = normalizeCasePrepBuddyHandoffText(
    rawTransaction.txHash ||
    rawTransaction.transactionHash ||
    rawTransaction.hash ||
    rawTransaction.Hash ||
    rawTransaction['Transaction Hash'] ||
    rawTransaction.TransactionHash ||
    ''
  );
  const toAddress = normalizeCasePrepBuddyHandoffText(
    rawTransaction.toAddress ||
    rawTransaction.to ||
    rawTransaction.ToAddress ||
    rawTransaction['To Address'] ||
    rawTransaction.destinationAddress ||
    rawTransaction.destination ||
    ''
  );
  if (!txHash || isLikelyCasePrepBuddyHandoffHeader(txHash)) return null;
  return {
    id: normalizeCasePrepBuddyHandoffText(rawTransaction.id) || `case-prep-handoff-row-${index + 1}`,
    sourceRowNumber: Number(rawTransaction.rowNumber) || index + 1,
    txHash,
    toAddress,
    network: normalizeCasePrepBuddyHandoffText(rawTransaction.network || rawTransaction.chainName || rawTransaction.ChainName),
    validationState: normalizeCasePrepBuddyHandoffText(rawTransaction.validationState),
    lookupStatus: normalizeCasePrepBuddyHandoffText(rawTransaction.lookupStatus),
    requiresToAddress: normalizeCasePrepBuddyHandoffText(rawTransaction.requiresToAddress)
  };
}

function parseCasePrepBuddyHandoffTsvTransactions(tsv = '') {
  const rows = String(tsv || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(line => line.split('\t').map(cell => normalizeCasePrepBuddyHandoffText(cell)))
    .filter(row => row.some(Boolean));

  return rows.map((cells, index) => {
    let txHash = '';
    let toAddress = '';
    if (cells.length >= 4) {
      txHash = cells[1] || '';
      toAddress = cells[3] || '';
    } else if (cells.length >= 2) {
      txHash = cells[0] || '';
      toAddress = cells[1] || '';
    } else {
      txHash = cells[0] || '';
    }
    return normalizeCasePrepBuddyHandoffTransaction({ txHash, toAddress, rowNumber: index + 1 }, index);
  }).filter(Boolean);
}

function normalizeCasePrepBuddyHandoffPayload(rawPayload = {}) {
  const source = rawPayload && typeof rawPayload === 'object' ? rawPayload : {};
  const transactionSources = [];
  if (Array.isArray(source.transactions)) transactionSources.push(...source.transactions);
  if (Array.isArray(source.rows)) transactionSources.push(...source.rows);

  const parsedTransactions = transactionSources
    .map((transaction, index) => normalizeCasePrepBuddyHandoffTransaction(transaction, index))
    .filter(Boolean);
  if (parsedTransactions.length === 0 && source.tsv) {
    parsedTransactions.push(...parseCasePrepBuddyHandoffTsvTransactions(source.tsv));
  }

  const seen = new Set();
  const transactions = [];
  parsedTransactions.forEach((transaction) => {
    const key = `${normalizeCasePrepBuddyHandoffKey(transaction.txHash)}||${normalizeCasePrepBuddyHandoffKey(transaction.toAddress)}`;
    if (!transaction.txHash || seen.has(key)) return;
    seen.add(key);
    transactions.push({ ...transaction, rowNumber: transactions.length + 2 });
  });

  const prepRunTitle = normalizeCasePrepBuddyHandoffText(source.prepRunTitle || source.title || source.caseName);
  const suggestedCaseName = normalizeCasePrepBuddyHandoffText(source.suggestedCaseName || source.caseName) ||
    (prepRunTitle ? `${prepRunTitle} — Case Buddy` : 'Case Prep Buddy handoff');

  return {
    schema: normalizeCasePrepBuddyHandoffText(source.schema) || CASE_PREP_TO_CASE_BUDDY_HANDOFF_SCHEMA,
    id: normalizeCasePrepBuddyHandoffText(source.id) || `case-prep-handoff-${Date.now().toString(36)}`,
    source: normalizeCasePrepBuddyHandoffText(source.source) || 'case-prep-buddy',
    createdAt: normalizeCasePrepBuddyHandoffText(source.createdAt) || new Date().toISOString(),
    prepRunId: normalizeCasePrepBuddyHandoffText(source.prepRunId),
    prepRunTitle,
    suggestedCaseName,
    transactionCount: transactions.length,
    transactions,
    tsv: normalizeCasePrepBuddyHandoffText(source.tsv),
    hashAndToAddressList: normalizeCasePrepBuddyHandoffText(source.hashAndToAddressList)
  };
}

function buildCasePrepBuddyHandoffFileName(payload = {}, existingFiles = []) {
  const base = normalizeCasePrepBuddyHandoffText(payload.suggestedCaseName || payload.prepRunTitle || 'Case Prep Buddy handoff')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .slice(0, 58) || 'Case Prep Buddy handoff';
  const used = new Set((Array.isArray(existingFiles) ? existingFiles : []).map(file => normalizeCasePrepBuddyHandoffText(file?.name)).filter(Boolean));
  if (!used.has(base)) return base;
  let index = 2;
  while (used.has(`${base} ${index}`)) index += 1;
  return `${base} ${index}`;
}

function buildCasePrepBuddyWorkbookState(payload = {}) {
  const workbook = createCaseWorkbookState();
  const mainSheet = workbook.sheets.find(sheet => caseFileBrowserIsLogicalSheetName(sheet.name, 'main')) || workbook.sheets[0];
  if (!mainSheet) return workbook;

  const mainState = createCaseSheetState({ includeHeaders: true });
  const neededRows = Math.max(100, (payload.transactions || []).length + 1);
  const data = normalizeCaseMatrix(mainState.data, neededRows, Math.max(26, CASE_COLUMN_HEADERS.length));
  CASE_COLUMN_HEADERS.forEach((header, index) => {
    data[0][index] = header;
  });
  (payload.transactions || []).forEach((transaction, index) => {
    const rowIndex = index + 1;
    data[rowIndex][1] = normalizeCasePrepBuddyHandoffText(transaction.txHash);
    data[rowIndex][3] = normalizeCasePrepBuddyHandoffText(transaction.toAddress);
  });

  mainSheet.state = {
    ...mainState,
    data,
    frozenTop: 1
  };
  return workbook;
}

function buildCasePrepBuddySupportState(payload = {}) {
  const now = new Date().toISOString();
  const transactionCount = Number(payload.transactionCount) || 0;
  const support = createDefaultCaseSupportState();
  support.caseDataRevision = 1;
  support.dirty = false;
  support.dirtySinceUtc = '';
  support.lastSavedRevision = 1;
  support.lastSavedUtc = now;
  support.lastMutation = {
    source: 'case_prep_buddy_handoff_imported',
    details: {
      transactionCount,
      prepRunId: payload.prepRunId || '',
      handoffId: payload.id || ''
    },
    utc: now,
    revision: 1
  };
  support.caseDetails = {
    ...(support.caseDetails || {}),
    caseName: payload.suggestedCaseName || payload.prepRunTitle || '',
    caseId: payload.prepRunId ? `Case Prep ${payload.prepRunId}`.slice(0, 80) : '',
    caseType: support.caseDetails?.caseType || ''
  };
  support.workflowState = {
    ...(support.workflowState || {}),
    casePrepBuddyHandoff: {
      schema: payload.schema || CASE_PREP_TO_CASE_BUDDY_HANDOFF_SCHEMA,
      id: payload.id || '',
      source: payload.source || 'case-prep-buddy',
      prepRunId: payload.prepRunId || '',
      prepRunTitle: payload.prepRunTitle || '',
      transactionCount,
      importedAtUtc: now,
      importedAsStartingMainTransactions: true
    }
  };
  support.checkResultsById = {
    ...(support.checkResultsById || {}),
    main_transactions: {
      id: 'main_transactions',
      status: transactionCount > 0 ? 'pass' : 'fail',
      message: transactionCount > 0
        ? `${transactionCount} starting main transaction${transactionCount === 1 ? '' : 's'} loaded from Case Prep Buddy.`
        : 'No starting main transactions were loaded from Case Prep Buddy.',
      details: {
        mode: 'case-prep-handoff',
        mainTransactionCount: transactionCount,
        trashRowCount: 0,
        source: 'case-prep-buddy',
        handoffId: payload.id || ''
      },
      revision: 1,
      checkedAtUtc: now
    }
  };
  return support;
}

export const caseBuddyProjectSheetMethods = {
    parseProjectJson(raw) {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('Failed to parse saved spreadsheet file metadata.', error);
        return null;
      }
    },

    createProjectFileMeta(name = null) {
      const timestamp = Date.now();
      return {
        id: createProjectFileId(),
        name: String(name || this.nextDefaultProjectFileName()).trim(),
        createdAt: timestamp,
        updatedAt: timestamp
      };
    },

    nextDefaultProjectFileName() {
      const usedNames = new Set(this.projectFiles.map(file => String(file?.name || '').trim()).filter(Boolean));
      let index = 1;

      while (usedNames.has(`Spreadsheet ${index}`)) {
        index += 1;
      }

      return `Spreadsheet ${index}`;
    },

    buildDuplicateProjectFileName(sourceName = 'Spreadsheet') {
      const baseName = String(sourceName || 'Spreadsheet').trim().replace(/ Copy(?: \d+)?$/, '');
      const usedNames = new Set(this.projectFiles.map(file => String(file?.name || '').trim()).filter(Boolean));
      let duplicateName = `${baseName} Copy`;
      let index = 2;

      while (usedNames.has(duplicateName)) {
        duplicateName = `${baseName} Copy ${index}`;
        index += 1;
      }

      return duplicateName;
    },

    getProjectInputStorageKey(fileId) {
      return `tool60_spreadsheet_project_${fileId}_input_v1`;
    },

    getProjectOutputStorageKey(fileId) {
      return `tool60_spreadsheet_project_${fileId}_output_v1`;
    },

    normalizeProjectFilesIndex(index) {
      const files = Array.isArray(index?.files)
        ? index.files
            .map((file, indexPosition) => ({
              id: typeof file?.id === 'string' && file.id ? file.id : createProjectFileId(),
              name: String(file?.name || `Spreadsheet ${indexPosition + 1}`),
              createdAt: Number.isFinite(Number(file?.createdAt)) ? Number(file.createdAt) : Date.now(),
              updatedAt: Number.isFinite(Number(file?.updatedAt)) ? Number(file.updatedAt) : Number.isFinite(Number(file?.createdAt)) ? Number(file.createdAt) : Date.now()
            }))
            .filter(file => file.id)
        : [];

      if (files.length === 0) {
        return null;
      }

      const activeFileId = files.some(file => file.id === index?.activeFileId)
        ? index.activeFileId
        : files[0].id;

      return {
        version: 1,
        activeFileId,
        files
      };
    },

    readProjectFilesIndex() {
      const raw = localStorage.getItem(PROJECT_FILES_INDEX_KEY);
      if (!raw) {
        return null;
      }

      return this.normalizeProjectFilesIndex(this.parseProjectJson(raw));
    },

    persistProjectFilesIndex(activeFileId = this.currentProjectFileId) {
      const normalized = {
        version: 1,
        activeFileId: activeFileId || this.projectFiles[0]?.id || '',
        files: this.projectFiles.map(file => ({
          id: file.id,
          name: file.name,
          createdAt: file.createdAt,
          updatedAt: file.updatedAt
        }))
      };

      const saveResult = this.safeCaseBuddyLocalStorageSet
        ? this.safeCaseBuddyLocalStorageSet(PROJECT_FILES_INDEX_KEY, JSON.stringify(normalized), { silent: true })
        : { ok: true };
      if (saveResult.ok === false) return null;
      return normalized;
    },

    migrateLegacyProjectData(targetFileId) {
      if (!targetFileId) {
        return;
      }

      const targetInputKey = this.getProjectInputStorageKey(targetFileId);
      if (localStorage.getItem(targetInputKey)) {
        return;
      }

      const modernWorkbookRaw = localStorage.getItem(LEGACY_INPUT_WORKBOOK_KEY);
      if (modernWorkbookRaw) {
        this.safeCaseBuddyLocalStorageSet?.(targetInputKey, modernWorkbookRaw) || localStorage.setItem(targetInputKey, modernWorkbookRaw);
        return;
      }

      const legacySingleSheetRaw = localStorage.getItem(LEGACY_SINGLE_SHEET_KEY);
      if (!legacySingleSheetRaw) {
        return;
      }

      const legacyState = this.parseProjectJson(legacySingleSheetRaw);
      if (!legacyState || !Array.isArray(legacyState.data)) {
        return;
      }

      const workbook = createBlankWorkbookState();
      workbook.sheets[0].state = legacyState;
      this.safeCaseBuddyLocalStorageSet?.(targetInputKey, JSON.stringify(workbook)) || localStorage.setItem(targetInputKey, JSON.stringify(workbook));
    },

    initializeProjectFiles() {
      const restoredIndex = this.readProjectFilesIndex();
      if (restoredIndex?.files?.length) {
        this.projectFiles = restoredIndex.files;
        this.currentProjectFileId = restoredIndex.activeFileId;
        this.selectedProjectFileId = restoredIndex.activeFileId;
        return;
      }

      const defaultFile = this.createProjectFileMeta('Spreadsheet 1');
      this.projectFiles = [defaultFile];
      this.currentProjectFileId = defaultFile.id;
      this.selectedProjectFileId = defaultFile.id;
      this.pendingProjectBootstrapFileId = defaultFile.id;
      this.persistProjectFilesIndex(defaultFile.id);
      this.migrateLegacyProjectData(defaultFile.id);
      this.createDefaultCaseStorageForFile(defaultFile.id);
    },

    readPendingCasePrepBuddyHandoffPayload() {
      if (typeof window === 'undefined' || !window.sessionStorage) return null;
      const raw = window.sessionStorage.getItem(CASE_PREP_TO_CASE_BUDDY_HANDOFF_STORAGE_KEY);
      if (!raw) return null;
      try {
        return normalizeCasePrepBuddyHandoffPayload(JSON.parse(raw));
      } catch (error) {
        console.warn('Case Buddy could not parse the Case Prep Buddy handoff payload.', error);
        return null;
      }
    },

    clearPendingCasePrepBuddyHandoffPayload() {
      if (typeof window === 'undefined' || !window.sessionStorage) return;
      try {
        window.sessionStorage.removeItem(CASE_PREP_TO_CASE_BUDDY_HANDOFF_STORAGE_KEY);
      } catch {
        // Best-effort cleanup only.
      }
    },

    dismissCasePrepBuddyHandoffNotice() {
      this.casePrepBuddyHandoffNotice = {
        visible: false,
        status: '',
        title: '',
        message: '',
        fileId: '',
        transactionCount: 0,
        importedAtUtc: ''
      };
    },

    createCaseFromCasePrepBuddyHandoff(rawPayload = {}, options = {}) {
      const payload = normalizeCasePrepBuddyHandoffPayload(rawPayload);
      if (!payload.transactionCount) {
        this.casePrepBuddyHandoffNotice = {
          visible: true,
          status: 'error',
          title: 'Case Prep handoff had no rows',
          message: 'Case Buddy received the handoff, but no transaction hashes were available to load.',
          fileId: '',
          transactionCount: 0,
          importedAtUtc: new Date().toISOString()
        };
        return { ok: false, reason: 'empty-handoff', payload };
      }

      const nextFile = this.createProjectFileMeta(buildCasePrepBuddyHandoffFileName(payload, this.projectFiles));
      const workbook = buildCasePrepBuddyWorkbookState(payload);
      const outputState = createCaseSheetState({ includeHeaders: false });
      const supportState = buildCasePrepBuddySupportState(payload);
      const inputKey = this.getProjectInputStorageKey(nextFile.id);
      const outputKey = this.getProjectOutputStorageKey(nextFile.id);
      const supportKey = this.getCaseSupportStorageKey(nextFile.id);
      const writes = [
        this.safeCaseBuddyLocalStorageSet(inputKey, JSON.stringify(workbook), { silent: true }),
        this.safeCaseBuddyLocalStorageSet(outputKey, JSON.stringify(outputState), { silent: true }),
        this.safeCaseBuddyLocalStorageSet(supportKey, JSON.stringify(supportState), { silent: true })
      ];
      const failed = writes.filter(result => result?.ok === false);
      if (failed.length > 0) {
        this.safeCaseBuddyLocalStorageRemove?.(inputKey);
        this.safeCaseBuddyLocalStorageRemove?.(outputKey);
        this.safeCaseBuddyLocalStorageRemove?.(supportKey);
        this.casePrepBuddyHandoffNotice = {
          visible: true,
          status: 'error',
          title: 'Could not create Case Buddy case',
          message: failed[0]?.quota
            ? 'Browser storage is full. Delete older saved cases from Browse cases, then return to Case Prep Buddy and send the handoff again.'
            : failed[0]?.message || 'Case Buddy could not save the new handoff case.',
          fileId: '',
          transactionCount: payload.transactionCount,
          importedAtUtc: new Date().toISOString()
        };
        return { ok: false, reason: 'storage-failed', failed, payload };
      }

      const currentIndex = this.projectFiles.findIndex(file => file.id === this.currentProjectFileId);
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : this.projectFiles.length;
      this.projectFiles.splice(insertIndex, 0, nextFile);
      const persistedIndex = this.persistProjectFilesIndex(nextFile.id);
      if (!persistedIndex) {
        this.projectFiles.splice(insertIndex, 1);
        this.safeCaseBuddyLocalStorageRemove?.(inputKey);
        this.safeCaseBuddyLocalStorageRemove?.(outputKey);
        this.safeCaseBuddyLocalStorageRemove?.(supportKey);
        this.casePrepBuddyHandoffNotice = {
          visible: true,
          status: 'error',
          title: 'Could not add handoff case to file list',
          message: 'The new case data was prepared, but the saved-case index could not be updated. Delete older cases and try again.',
          fileId: '',
          transactionCount: payload.transactionCount,
          importedAtUtc: new Date().toISOString()
        };
        return { ok: false, reason: 'index-failed', payload };
      }

      this.resetTransientProjectUiState?.();
      this.projectFileReadyState.input = false;
      this.projectFileReadyState.output = false;
      this.currentProjectFileId = nextFile.id;
      this.selectedProjectFileId = nextFile.id;
      this.pendingProjectBootstrapFileId = '';
      this.loadCaseSupportState(nextFile.id);
      this.caseSummary = createEmptyCaseSummary();
      this.casePrepBuddyHandoffNotice = {
        visible: true,
        status: 'success',
        title: 'Case Prep handoff loaded',
        message: `${payload.transactionCount} starting transaction${payload.transactionCount === 1 ? '' : 's'} loaded into Main Data. Continue with Identify networks, dates, and path labels, or run Auto.`,
        fileId: nextFile.id,
        transactionCount: payload.transactionCount,
        importedAtUtc: new Date().toISOString()
      };
      this.clearCaseProjectStorageNotice?.();
      if (!options.silent) {
        this.showCaseToast?.(`Created ${nextFile.name} from Case Prep Buddy.`);
      }
      this.queueCaseSummaryRefresh?.(240);
      return { ok: true, file: nextFile, payload };
    },

    consumePendingCasePrepBuddyHandoff(options = {}) {
      const payload = this.readPendingCasePrepBuddyHandoffPayload();
      if (!payload) return { ok: false, reason: 'no-payload' };
      const result = this.createCaseFromCasePrepBuddyHandoff(payload, options);
      if (result?.ok) {
        this.clearPendingCasePrepBuddyHandoffPayload();
      }
      return result;
    },

    hasStoredProjectInput(fileId = this.currentProjectFileId) {
      return !!(fileId && localStorage.getItem(this.getProjectInputStorageKey(fileId)));
    },

    hasStoredProjectOutput(fileId = this.currentProjectFileId) {
      return !!(fileId && localStorage.getItem(this.getProjectOutputStorageKey(fileId)));
    },

    getProjectFileStorageStats(fileId = this.currentProjectFileId) {
      const id = normalizeCell(fileId);
      const keys = [
        this.getProjectInputStorageKey?.(id) || '',
        this.getProjectOutputStorageKey?.(id) || '',
        this.getCaseSupportStorageKey?.(id) || ''
      ].filter(Boolean);
      let bytes = 0;
      let itemCount = 0;
      const items = [];
      keys.forEach((key) => {
        try {
          const value = localStorage.getItem(key);
          if (value == null) return;
          const itemBytes = (key.length + value.length) * 2;
          bytes += itemBytes;
          itemCount += 1;
          items.push({ key, bytes: itemBytes, sizeLabel: this.formatCaseBuddyStorageBytes?.(itemBytes) || `${itemBytes} B` });
        } catch {
          // Ignore storage-stat read failures.
        }
      });
      return {
        fileId: id,
        bytes,
        itemCount,
        items,
        sizeLabel: this.formatCaseBuddyStorageBytes?.(bytes) || `${bytes} B`
      };
    },


    readCaseFileBrowserSupportState(fileId = this.currentProjectFileId) {
      if (fileId && fileId === this.currentProjectFileId) {
        return this.caseSupportState || {};
      }
      const storageKey = this.getCaseSupportStorageKey?.(fileId) || '';
      if (!storageKey) return {};
      const raw = localStorage.getItem(storageKey);
      if (!raw) return {};
      return this.parseProjectJson(raw) || {};
    },

    readCaseFileBrowserWorkbook(fileId = this.currentProjectFileId) {
      const storageKey = this.getProjectInputStorageKey?.(fileId) || '';
      if (!storageKey) return null;
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = this.parseProjectJson(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (Array.isArray(parsed.sheets)) return parsed;
      if (Array.isArray(parsed.data)) {
        return {
          version: 0,
          activeSheetId: 'legacy-main',
          sheets: [{ id: 'legacy-main', name: 'Main Data', state: parsed }]
        };
      }
      return parsed;
    },

    getCaseFileBrowserSheetDataFromWorkbook(workbook = null, role = 'main') {
      if (!workbook || typeof workbook !== 'object') return CASE_FILE_BROWSER_DEFAULT_ROWS;
      const sheets = Array.isArray(workbook.sheets) ? workbook.sheets : [];
      const match = sheets.find(sheet => this.isLogicalSheetName?.(sheet.name, role) || caseFileBrowserIsLogicalSheetName(sheet.name, role)) || null;
      const fallback = role === 'main'
        ? sheets.find(sheet => sheet.id === workbook.activeSheetId) || sheets[0] || null
        : null;
      const sheet = match || fallback;
      const state = sheet?.state || sheet?.sheetState || sheet || null;
      return Array.isArray(state?.data) ? state.data : CASE_FILE_BROWSER_DEFAULT_ROWS;
    },

    getCaseFileBrowserMatrixForFile(fileId = this.currentProjectFileId, role = 'main') {
      if (fileId && fileId === this.currentProjectFileId && this.projectFileReadyState?.input && typeof this.getMatrixForLogicalSheet === 'function') {
        const current = this.getMatrixForLogicalSheet(role);
        if (Array.isArray(current) && current.length) return current;
      }
      const workbook = this.readCaseFileBrowserWorkbook(fileId);
      return this.getCaseFileBrowserSheetDataFromWorkbook(workbook, role);
    },

    buildCaseFileBrowserSummaryForFile(file = {}) {
      const fileId = normalizeCell(file?.id);
      const supportState = this.readCaseFileBrowserSupportState(fileId);
      const caseDetails = supportState?.caseDetails && typeof supportState.caseDetails === 'object' ? supportState.caseDetails : {};
      const mainData = normalizeCaseMatrix(this.getCaseFileBrowserMatrixForFile(fileId, 'main'));
      const rawData = normalizeCaseMatrix(this.getCaseFileBrowserMatrixForFile(fileId, 'raw'));
      const mainEntries = parseCaseEntries(mainData);
      const rawEntries = parseCaseEntries(rawData);
      const topMainEntries = buildTopLevelMainEntryDescriptors(mainData);
      const topMainStartRows = new Set(topMainEntries.map(entry => entry.startRow));
      const meaningfulMainRows = mainData.slice(1).filter(row => Array.isArray(row) && rowHasMeaningfulData(row));
      const meaningfulRawRows = rawData.slice(1).filter(row => Array.isArray(row) && rowHasMeaningfulData(row));
      const mainHashRows = meaningfulMainRows.filter(row => normalizeCell(row?.[1])).length;
      const rawHashRows = meaningfulRawRows.filter(row => normalizeCell(row?.[1])).length;
      const subtransactionCount = Math.max(0, mainEntries.filter(entry => !topMainStartRows.has(entry.startRow)).length);
      const firstMainRow = caseFileBrowserFirstMeaningfulRow(meaningfulMainRows);
      const counts = caseFileBrowserCollectCounts(mainData, topMainEntries, mainEntries);
      const dateRange = caseFileBrowserMakeDateRange(meaningfulMainRows);
      let saplingAnalysis = null;
      try {
        saplingAnalysis = buildSaplingPreparationAnalysis(mainData, rawData, supportState?.workflowState || {});
      } catch {
        saplingAnalysis = null;
      }
      const preparedSaplingCount = Number(saplingAnalysis?.processedCount) || 0;
      const unprocessedSaplingCount = Number(saplingAnalysis?.unprocessedCount) || 0;
      const rawMissingSaplingCount = Number(saplingAnalysis?.rawMissing) || 0;
      const reprocessAvailableCount = Number(saplingAnalysis?.reprocessAvailableCount) || 0;
      const hashExamples = topMainEntries
        .map(entry => normalizeCell(entry.hash || entry.entryRows?.[0]?.row?.[1]))
        .filter(Boolean)
        .slice(0, 3)
        .map(hash => abbreviateCaseBuddyHash(hash));
      const displayName = normalizeCell(caseDetails.caseName) || normalizeCell(file.name) || 'Unnamed case';
      const caseId = normalizeCell(caseDetails.caseId);
      const caseType = normalizeCell(caseDetails.caseType);
      const mainTransactionCount = topMainEntries.length;
      const updatedAt = Number(file.updatedAt) || 0;
      const createdAt = Number(file.createdAt) || 0;
      const storageStats = this.getProjectFileStorageStats(fileId);
      const hasData = mainEntries.length > 0 || rawEntries.length > 0 || meaningfulMainRows.length > 0 || meaningfulRawRows.length > 0;
      const chainText = counts.chainNames.map(item => item.label).join(' ');
      const tokenText = counts.tokenSymbols.map(item => item.label).join(' ');
      const notes = [
        mainTransactionCount > 0 ? `${mainTransactionCount} main transaction${mainTransactionCount === 1 ? '' : 's'}` : 'No main transactions',
        subtransactionCount > 0 ? `${subtransactionCount} subtransaction${subtransactionCount === 1 ? '' : 's'}` : '',
        preparedSaplingCount > 0 ? `${preparedSaplingCount} prepared sapling${preparedSaplingCount === 1 ? '' : 's'}` : '',
        counts.swapBridgeSummary,
        counts.btcMainTransactionCount > 0 ? `${counts.btcMainTransactionCount} BTC main` : '',
        chainText || tokenText,
        dateRange.label
      ].filter(Boolean).join(' · ');

      return {
        id: fileId,
        fileId,
        name: normalizeCell(file.name) || displayName,
        displayName,
        caseName: normalizeCell(caseDetails.caseName),
        caseId,
        caseType,
        createdAt,
        updatedAt,
        updatedLabel: updatedAt ? this.formatProjectFileDate?.(updatedAt) || new Date(updatedAt).toLocaleString() : '',
        createdLabel: createdAt ? this.formatProjectFileDate?.(createdAt) || new Date(createdAt).toLocaleString() : '',
        isCurrent: fileId === this.currentProjectFileId,
        isDirty: fileId === this.currentProjectFileId ? !!this.caseSupportState?.dirty : !!supportState?.dirty,
        storageBytes: storageStats.bytes || 0,
        storageSizeLabel: storageStats.sizeLabel || '',
        storageItemCount: storageStats.itemCount || 0,
        storageItems: storageStats.items || [],
        caseDataRevision: Number(supportState?.caseDataRevision) || 0,
        lastSavedUtc: normalizeCell(supportState?.lastSavedUtc),
        mainEntryCount: mainEntries.length,
        mainTransactionCount,
        subtransactionCount,
        mainHashRows,
        mainRowCount: meaningfulMainRows.length,
        rawEntryCount: rawEntries.length,
        rawHashRows,
        rawRowCount: meaningfulRawRows.length,
        preparedSaplingCount,
        unprocessedSaplingCount,
        rawMissingSaplingCount,
        reprocessAvailableCount,
        pathEndingRows: counts.pathEndingRows,
        qualifiedOutputRows: counts.qualifiedOutputRows,
        fundsFollowedRows: counts.fundsFollowedRows,
        swapCount: counts.swapCount,
        bridgeCount: counts.bridgeCount,
        swapBridgeCount: counts.swapBridgeCount,
        swapEntryCount: counts.swapEntryCount,
        bridgeEntryCount: counts.bridgeEntryCount,
        addressCount: counts.addressCount,
        missingPathLabels: counts.missingPathLabels,
        swapBridgeSelfTransferCount: counts.swapBridgeSelfTransferCount,
        swapExamples: counts.swapExamples,
        bridgeExamples: counts.bridgeExamples,
        swapBridgeSummary: counts.swapBridgeSummary,
        btcMainTransactionCount: counts.btcMainTransactionCount,
        ethMainTransactionCount: counts.ethMainTransactionCount,
        chainNames: counts.chainNames,
        tokenSymbols: counts.tokenSymbols,
        dateRangeLabel: dateRange.label,
        hashExamples,
        firstHash: normalizeCell(firstMainRow?.[1]),
        firstHashLabel: abbreviateCaseBuddyHash(firstMainRow?.[1]),
        firstToAddress: normalizeCell(firstMainRow?.[3]),
        notes,
        hasData,
        statusLabel: !hasData
          ? 'Blank / no case rows yet'
          : preparedSaplingCount > 0
            ? 'Sapling data present'
            : mainTransactionCount > 0
              ? 'Starting transactions present'
              : 'Stored case data present',
        searchText: [
          file.name,
          displayName,
          caseId,
          caseType,
          notes,
          chainText,
          tokenText,
          counts.swapEntryCount > 0 ? `swapped swap swaps ${counts.swapEntryCount} ${counts.swapExamples.join(' ')}` : '',
          counts.bridgeEntryCount > 0 ? `bridged bridge bridges ${counts.bridgeEntryCount} ${counts.bridgeExamples.join(' ')}` : '',
          hashExamples.join(' '),
          normalizeCell(firstMainRow?.[1]),
          normalizeCell(firstMainRow?.[3])
        ].join(' ').toLowerCase()
      };
    },

    async refreshCaseFileBrowser(options = {}) {
      const files = Array.isArray(this.projectFiles) ? this.projectFiles.slice() : [];
      const token = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const preserveSelection = options.preserveSelection !== false;
      this.caseFileBrowserScanToken = token;
      this.caseFileBrowserLoading = true;
      this.caseFileBrowserStatus = files.length
        ? `Scanning ${files.length} saved case file${files.length === 1 ? '' : 's'}…`
        : 'No saved case files found.';
      this.caseFileBrowserProgress = { done: 0, total: files.length };
      if (!preserveSelection) this.caseFileBrowserSelectedId = this.currentProjectFileId || files[0]?.id || '';
      this.caseFileBrowserRows = [];
      if (typeof this.yieldToCaseUi === 'function') await this.yieldToCaseUi({ paint: true, frames: 1, minDelayMs: 16 });

      const rows = [];
      for (let index = 0; index < files.length; index += 1) {
        if (this.caseFileBrowserScanToken !== token) return [];
        const file = files[index];
        let row = null;
        try {
          row = this.buildCaseFileBrowserSummaryForFile(file);
        } catch (error) {
          row = {
            id: file.id,
            fileId: file.id,
            name: normalizeCell(file.name) || 'Unreadable case',
            displayName: normalizeCell(file.name) || 'Unreadable case',
            createdAt: Number(file.createdAt) || 0,
            updatedAt: Number(file.updatedAt) || 0,
            updatedLabel: file.updatedAt ? this.formatProjectFileDate?.(file.updatedAt) || '' : '',
            isCurrent: file.id === this.currentProjectFileId,
            storageBytes: this.getProjectFileStorageStats(file.id).bytes || 0,
            storageSizeLabel: this.getProjectFileStorageStats(file.id).sizeLabel || '',
            storageItemCount: this.getProjectFileStorageStats(file.id).itemCount || 0,
            hasData: false,
            mainEntryCount: 0,
            mainTransactionCount: 0,
            subtransactionCount: 0,
            rawEntryCount: 0,
            preparedSaplingCount: 0,
            pathEndingRows: 0,
            qualifiedOutputRows: 0,
            fundsFollowedRows: 0,
            addressCount: 0,
            missingPathLabels: 0,
            swapCount: 0,
            bridgeCount: 0,
            swapBridgeCount: 0,
            swapEntryCount: 0,
            bridgeEntryCount: 0,
            swapBridgeSelfTransferCount: 0,
            swapExamples: [],
            bridgeExamples: [],
            swapBridgeSummary: '',
            btcMainTransactionCount: 0,
            ethMainTransactionCount: 0,
            chainNames: [],
            tokenSymbols: [],
            hashExamples: [],
            statusLabel: 'Could not summarize this saved case',
            notes: error?.message || 'Stored workbook could not be parsed.',
            searchText: [file.name, error?.message].join(' ').toLowerCase(),
            error: error?.message || 'Summary failed.'
          };
        }
        rows.push(row);
        this.caseFileBrowserRows = rows.slice();
        this.caseFileBrowserProgress = { done: index + 1, total: files.length };
        this.caseFileBrowserStatus = `Scanned ${index + 1}/${files.length} saved case file${files.length === 1 ? '' : 's'}…`;
        if (typeof this.yieldToCaseUi === 'function' && (index % 2 === 1 || index === files.length - 1)) {
          await this.yieldToCaseUi({ paint: true, frames: 1, minDelayMs: 8 });
        }
      }

      if (this.caseFileBrowserScanToken !== token) return rows;
      this.caseFileBrowserLoading = false;
      this.caseFileBrowserLastScannedAtUtc = new Date().toISOString();
      const swapCaseCount = rows.filter(row => (Number(row.swapCount) || 0) > 0).length;
      const bridgeCaseCount = rows.filter(row => (Number(row.bridgeCount) || 0) > 0).length;
      const signalSummary = [
        swapCaseCount ? `${swapCaseCount} with swaps` : '',
        bridgeCaseCount ? `${bridgeCaseCount} with bridges` : ''
      ].filter(Boolean).join(' · ');
      this.caseFileBrowserStatus = rows.length
        ? `Scanned ${rows.length} saved case file${rows.length === 1 ? '' : 's'}.${signalSummary ? ` ${signalSummary}.` : ''} Select a row to inspect or load it.`
        : 'No saved case files found.';
      const selectedStillExists = rows.some(row => row.id === this.caseFileBrowserSelectedId);
      if (!selectedStillExists) this.caseFileBrowserSelectedId = this.currentProjectFileId || rows[0]?.id || '';
      return rows;
    },

    async openCaseFileBrowserModal() {
      this.showCaseFileBrowserModal = true;
      if (!this.caseFileBrowserSelectedId) this.caseFileBrowserSelectedId = this.currentProjectFileId || this.projectFiles[0]?.id || '';
      await this.refreshCaseFileBrowser({ preserveSelection: true });
    },

    cancelCaseFileBrowserModal() {
      this.caseFileBrowserScanToken = `${Date.now()}-cancelled`;
      this.caseFileBrowserLoading = false;
      this.showCaseFileBrowserModal = false;
    },

    selectCaseFileBrowserRow(fileId = '') {
      const id = normalizeCell(fileId);
      if (!id) return;
      this.caseFileBrowserSelectedId = id;
    },

    loadCaseFileBrowserRow(fileId = '') {
      const targetId = normalizeCell(fileId || this.caseFileBrowserSelectedId);
      if (!targetId) return false;
      if (targetId === this.currentProjectFileId) {
        this.showCaseFileBrowserModal = false;
        return true;
      }
      if (typeof this.maybeConfirmProjectSwitch === 'function' && !this.maybeConfirmProjectSwitch(targetId)) {
        return false;
      }
      this.showCaseFileBrowserModal = false;
      this.switchToProjectFile(targetId);
      return true;
    },

    loadSelectedCaseFileBrowserRow() {
      return this.loadCaseFileBrowserRow(this.caseFileBrowserSelectedId);
    },

    markCurrentProjectSaved() {
      const fileMeta = this.currentProjectFileMeta;
      if (!fileMeta) {
        return;
      }

      fileMeta.updatedAt = Date.now();
      this.persistProjectFilesIndex();
    },

    saveCurrentProjectFile({ persistInput = true, persistOutput = true, markSavedAt = true } = {}) {
      if (!this.currentProjectFileId) {
        return null;
      }

      let workbook = null;
      let saveFailed = false;
      const failures = [];

      if (persistInput) {
        try {
          workbook = this.$refs.inputSpreadsheet?.saveWorkbook?.(this.currentProjectInputStorageKey) || null;
          const inputSaveResult = workbook?.__storageSaveResult || this.$refs.inputSpreadsheet?.lastWorkbookSaveResult || null;
          if (inputSaveResult?.ok === false) {
            saveFailed = true;
            failures.push(inputSaveResult);
          }
        } catch (error) {
          saveFailed = true;
          const result = {
            ok: false,
            storageKey: this.currentProjectInputStorageKey,
            quota: this.isCaseBuddyStorageQuotaError?.(error) !== false,
            errorName: String(error?.name || 'StorageError'),
            message: error?.message || 'Input workbook save failed.'
          };
          this.handleProjectWorkbookStorageError?.(result);
          failures.push(result);
        }
      }

      if (persistOutput && this.$refs.outputSpreadsheet) {
        try {
          persistSpreadsheetState(this.$refs.outputSpreadsheet, this.currentProjectOutputStorageKey);
        } catch (error) {
          saveFailed = true;
          const result = {
            ok: false,
            storageKey: this.currentProjectOutputStorageKey,
            quota: this.isCaseBuddyStorageQuotaError?.(error) !== false,
            errorName: String(error?.name || 'StorageError'),
            message: error?.message || 'Output sheet save failed.'
          };
          this.handleProjectWorkbookStorageError?.(result);
          failures.push(result);
        }
      }

      if (saveFailed) {
        if (workbook && typeof workbook === 'object') {
          try {
            Object.defineProperty(workbook, '__caseBuddySaveFailed', {
              value: true,
              enumerable: false,
              configurable: true
            });
            Object.defineProperty(workbook, '__caseBuddySaveFailures', {
              value: failures,
              enumerable: false,
              configurable: true
            });
          } catch {
            workbook.__caseBuddySaveFailed = true;
            workbook.__caseBuddySaveFailures = failures;
          }
        }
        return workbook;
      }

      if (markSavedAt) {
        this.markCurrentProjectSaved();
        if (persistInput) {
          this.markCaseSaved();
        } else {
          this.persistCaseSupportState();
        }
      }

      this.clearCaseProjectStorageNotice?.();
      return workbook;
    },

    saveSpreadsheetFromEvent() {
      return this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
    },

    handleOutputSheetSaveRequest() {
      return this.saveCurrentProjectFile({ persistInput: false, persistOutput: true, markSavedAt: true });
    },

    handleInputWorkbookReady(hotInstance = null) {
      this.projectFileReadyState.input = true;
      this.casePageHydrationMessage = this.projectFileReadyState.output
        ? 'Spreadsheet tabs are ready. Finalizing case workspace…'
        : 'Input workbook is ready. Loading output review sheet…';
      this.registerCaseMutationWatchers(hotInstance || this.getInputHot(), 'input-workbook');
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.ensureCurrentProjectSnapshotExists();
      this.queueCaseSummaryRefresh(220);
      this.completeCaseInitialHydrationIfReady({ delay: 260 });
    },

    handleOutputSheetReady(hotInstance = null) {
      this.projectFileReadyState.output = true;
      this.casePageHydrationMessage = this.projectFileReadyState.input
        ? 'Spreadsheet tabs are ready. Finalizing case workspace…'
        : 'Output review sheet is ready. Loading input workbook…';
      this.registerOutputMutationWatchers(hotInstance || this.$refs.outputSpreadsheet?.hotInstance);
      if (this.currentProjectOutputStorageKey) {
        restoreSpreadsheetState(this.$refs.outputSpreadsheet, this.currentProjectOutputStorageKey);
      }
      this.$refs.outputSpreadsheet?.refreshDimensions?.();
      this.ensureCurrentProjectSnapshotExists();
      this.queueCaseSummaryRefresh(220);
      this.completeCaseInitialHydrationIfReady({ delay: 260 });
    },

    ensureCurrentProjectSnapshotExists() {
      if (!this.currentProjectFileId || this.pendingProjectBootstrapFileId !== this.currentProjectFileId) {
        return;
      }

      if (!this.projectFileReadyState.input || !this.projectFileReadyState.output) {
        return;
      }

      const hasStoredInput = this.hasStoredProjectInput(this.currentProjectFileId);
      const hasStoredOutput = this.hasStoredProjectOutput(this.currentProjectFileId);

      if (hasStoredInput && hasStoredOutput) {
        this.pendingProjectBootstrapFileId = '';
        return;
      }

      this.$nextTick(() => {
        if (this.pendingProjectBootstrapFileId !== this.currentProjectFileId) {
          return;
        }

        const saved = this.saveCurrentProjectFile({ persistInput: true, persistOutput: true, markSavedAt: true });
        if (!saved?.__caseBuddySaveFailed) this.pendingProjectBootstrapFileId = '';
      });
    },

    resetTransientProjectUiState() {
      this.closeShowcaseMenu();
      this.showBorderModal = false;
      this.showPathOperationModal = false;
      this.pathOperationRunning = false;
      this.searchQuery = '';
      this.matchCount = 0;
      this.lastSelection = null;
      this.freezeTop = false;
      this.freezeLeft = false;
      this.formulaBarEditing = false;
      this.formulaTargetCell = null;
      this.formulaCapturedReferences = [];
      this.formulaBarDraft = '';
      this.formulaBarBaseDraft = '';
      this.finishColorPickerSession({ restoreSelection: false });
      this.resetCaseInvestigationVisualizerState?.({ close: true, reason: 'project-switch' });
    },

    maybeConfirmProjectSwitch(targetFileId) {
      if (!targetFileId || targetFileId === this.currentProjectFileId) {
        return false;
      }

      if (this.autoSaveEnabled) {
        const saved = this.saveCurrentProjectFile();
        if (saved?.__caseBuddySaveFailed) {
          const targetName = this.projectFiles.find(file => file.id === targetFileId)?.name || 'the selected file';
          const confirmed = window.confirm(`Case Buddy could not save the current case because browser storage is full. Load "${targetName}" anyway?\n\nUse Browse cases to delete older saved cases and free storage.`);
          if (!confirmed) {
            this.selectedProjectFileId = this.currentProjectFileId;
            return false;
          }
        }
        return true;
      }

      const targetName = this.projectFiles.find(file => file.id === targetFileId)?.name || 'the selected file';
      const confirmed = window.confirm(`Auto-save is off. Load "${targetName}" without saving the current file first?`);
      if (!confirmed) {
        this.selectedProjectFileId = this.currentProjectFileId;
        return false;
      }

      return true;
    },

    switchToProjectFile(targetFileId, { bootstrapBlank = false } = {}) {
      if (!targetFileId || targetFileId === this.currentProjectFileId) {
        return;
      }

      this.resetTransientProjectUiState();
      this.projectFileReadyState.input = false;
      this.projectFileReadyState.output = false;
      this.startCaseInitialHydration({ message: 'Loading selected case file…' });
      this.currentProjectFileId = targetFileId;
      this.selectedProjectFileId = targetFileId;
      this.pendingProjectBootstrapFileId = bootstrapBlank ? targetFileId : '';
      if (bootstrapBlank) {
        this.createDefaultCaseStorageForFile(targetFileId);
      }
      this.loadCaseSupportState(targetFileId);
      this.caseSummary = createEmptyCaseSummary();
      this.persistProjectFilesIndex(targetFileId);
      this.queueCaseSummaryRefresh(240);
    },

    loadSelectedProjectFile() {
      if (!this.canLoadSelectedProjectFile) {
        return;
      }

      if (!this.maybeConfirmProjectSwitch(this.selectedProjectFileId)) {
        return;
      }

      this.switchToProjectFile(this.selectedProjectFileId);
    },

    createBlankProjectFile() {
      if (this.autoSaveEnabled) {
        const saved = this.saveCurrentProjectFile();
        if (saved?.__caseBuddySaveFailed) {
          const confirmed = window.confirm('Case Buddy could not save the current case because browser storage is full. Create a new blank file anyway?\n\nUse Browse cases to delete older saved cases first if you need to preserve the current one.');
          if (!confirmed) return;
        }
      } else {
        const confirmed = window.confirm('Auto-save is off. Create a new blank file without saving the current one first?');
        if (!confirmed) {
          return;
        }
      }

      const suggestedName = this.nextDefaultProjectFileName();
      const inputName = window.prompt('Name the new spreadsheet file.', suggestedName);
      if (inputName === null) {
        return;
      }

      const trimmedName = inputName.trim() || suggestedName;
      const nextFile = this.createProjectFileMeta(trimmedName);
      const currentIndex = this.projectFiles.findIndex(file => file.id === this.currentProjectFileId);
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : this.projectFiles.length;
      this.projectFiles.splice(insertIndex, 0, nextFile);
      const defaultStorageResult = this.createDefaultCaseStorageForFile(nextFile.id);
      if (defaultStorageResult?.ok === false) {
        this.projectFiles.splice(insertIndex, 1);
        this.persistProjectFilesIndex(this.currentProjectFileId);
        this.showCaseToast?.('Browser storage is full. Delete older saved cases before creating a new one.');
        return;
      }
      this.persistProjectFilesIndex(this.currentProjectFileId);
      this.switchToProjectFile(nextFile.id, { bootstrapBlank: false });
    },

    saveCurrentProjectAsNewFile() {
      const sourceName = this.currentProjectFileName;
      const suggestedName = this.buildDuplicateProjectFileName(sourceName);
      const inputName = window.prompt('Save the current spreadsheet as a new file.', suggestedName);
      if (inputName === null) {
        return;
      }

      const trimmedName = inputName.trim() || suggestedName;
      const nextFile = this.createProjectFileMeta(trimmedName);
      const currentIndex = this.projectFiles.findIndex(file => file.id === this.currentProjectFileId);
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : this.projectFiles.length;
      this.projectFiles.splice(insertIndex, 0, nextFile);

      let saveFailed = false;
      try {
        const savedWorkbook = this.$refs.inputSpreadsheet?.saveWorkbook?.(this.getProjectInputStorageKey(nextFile.id));
        saveFailed = !!(savedWorkbook?.__storageSaveResult?.ok === false || this.$refs.inputSpreadsheet?.lastWorkbookSaveResult?.ok === false);
      } catch (error) {
        saveFailed = true;
        this.handleProjectWorkbookStorageError?.({
          storageKey: this.getProjectInputStorageKey(nextFile.id),
          quota: this.isCaseBuddyStorageQuotaError?.(error) !== false,
          errorName: String(error?.name || 'StorageError'),
          message: error?.message || 'Could not save the duplicated input workbook.'
        });
      }
      if (!saveFailed && this.$refs.outputSpreadsheet) {
        try {
          persistSpreadsheetState(this.$refs.outputSpreadsheet, this.getProjectOutputStorageKey(nextFile.id));
        } catch (error) {
          saveFailed = true;
          this.handleProjectWorkbookStorageError?.({
            storageKey: this.getProjectOutputStorageKey(nextFile.id),
            quota: this.isCaseBuddyStorageQuotaError?.(error) !== false,
            errorName: String(error?.name || 'StorageError'),
            message: error?.message || 'Could not save the duplicated output sheet.'
          });
        }
      }
      if (!saveFailed) {
        const supportSaveResult = this.cloneCurrentCaseSupportStateToFile(nextFile.id);
        saveFailed = supportSaveResult?.ok === false;
      }
      if (saveFailed) {
        this.projectFiles.splice(insertIndex, 1);
        this.safeCaseBuddyLocalStorageRemove?.(this.getProjectInputStorageKey(nextFile.id));
        this.safeCaseBuddyLocalStorageRemove?.(this.getProjectOutputStorageKey(nextFile.id));
        this.safeCaseBuddyLocalStorageRemove?.(this.getCaseSupportStorageKey(nextFile.id));
        this.persistProjectFilesIndex(this.currentProjectFileId);
        this.showCaseToast?.('Browser storage is full. Save As was cancelled; delete older cases first.');
        return;
      }

      nextFile.updatedAt = Date.now();
      this.switchToProjectFile(nextFile.id);
    },

    renameCurrentProjectFile() {
      const currentFile = this.currentProjectFileMeta;
      if (!currentFile) {
        return;
      }

      const inputName = window.prompt('Rename the current spreadsheet file.', currentFile.name);
      if (inputName === null) {
        return;
      }

      const trimmedName = inputName.trim();
      if (!trimmedName) {
        return;
      }

      currentFile.name = trimmedName.slice(0, 48);
      currentFile.updatedAt = Date.now();
      this.persistProjectFilesIndex();
    },

    deleteProjectFileById(fileId = '', options = {}) {
      const targetId = normalizeCell(fileId);
      if (!targetId || this.projectFiles.length <= 1) return false;
      const fileIndex = this.projectFiles.findIndex(file => file.id === targetId);
      if (fileIndex < 0) return false;
      const file = this.projectFiles[fileIndex];
      if (options.confirm !== false) {
        const confirmed = window.confirm(`Delete "${file.name}" and all of its saved data? This cannot be undone.`);
        if (!confirmed) return false;
      }

      this.safeCaseBuddyLocalStorageRemove?.(this.getProjectInputStorageKey(file.id)) || localStorage.removeItem(this.getProjectInputStorageKey(file.id));
      this.safeCaseBuddyLocalStorageRemove?.(this.getProjectOutputStorageKey(file.id)) || localStorage.removeItem(this.getProjectOutputStorageKey(file.id));
      this.safeCaseBuddyLocalStorageRemove?.(this.getCaseSupportStorageKey(file.id)) || localStorage.removeItem(this.getCaseSupportStorageKey(file.id));
      this.projectFiles.splice(fileIndex, 1);

      const fallbackFile = this.projectFiles[Math.max(0, fileIndex - 1)] || this.projectFiles[0] || null;
      this.persistProjectFilesIndex(targetId === this.currentProjectFileId ? fallbackFile?.id : this.currentProjectFileId);
      this.clearCaseProjectStorageNotice?.();

      if (targetId === this.currentProjectFileId && fallbackFile) {
        this.switchToProjectFile(fallbackFile.id);
      }

      if (this.showCaseFileBrowserModal) {
        this.caseFileBrowserSelectedId = targetId === this.caseFileBrowserSelectedId
          ? (fallbackFile?.id || this.currentProjectFileId || this.projectFiles[0]?.id || '')
          : this.caseFileBrowserSelectedId;
        this.refreshCaseFileBrowser?.({ preserveSelection: true });
      }

      this.showCaseToast?.(`Deleted "${file.name}" and freed saved browser storage.`);
      return true;
    },

    deleteSelectedCaseFileBrowserRow() {
      const row = this.caseFileBrowserSelectedRow;
      if (!row?.fileId || row.isCurrent) return false;
      return this.deleteProjectFileById(row.fileId);
    },

    deleteCurrentProjectFile() {
      if (this.projectFiles.length <= 1) {
        window.alert('At least one spreadsheet file is required.');
        return false;
      }

      const currentFile = this.currentProjectFileMeta;
      if (!currentFile) {
        return false;
      }

      return this.deleteProjectFileById(currentFile.id);
    },

    toColumnLabel(index) {
      let label = '';
      let current = index;

      while (current >= 0) {
        label = String.fromCharCode((current % 26) + 65) + label;
        current = Math.floor(current / 26) - 1;
      }

      return label;
    },

    isValidFormulaTarget(cell) {
      return !!cell && Number.isInteger(cell.row) && Number.isInteger(cell.col) && cell.row >= 0 && cell.col >= 0;
    },

    cloneFormulaTarget(cell) {
      return this.isValidFormulaTarget(cell) ? { row: cell.row, col: cell.col } : null;
    },

    lockFormulaTarget(preferredCell = null) {
      const candidate = this.cloneFormulaTarget(preferredCell)
        || this.cloneFormulaTarget(this.formulaTargetCell)
        || this.cloneFormulaTarget(this.getSelectedAnchorCell());

      if (!candidate) {
        return null;
      }

      this.formulaTargetCell = candidate;
      return candidate;
    },

    clearFormulaRefocusTimer() {
      if (this.formulaRefocusTimer) {
        window.clearTimeout(this.formulaRefocusTimer);
        this.formulaRefocusTimer = null;
      }
    },

    queueFormulaInputRefocus(delay = 110) {
      this.clearFormulaRefocusTimer();
      this.formulaRefocusTimer = window.setTimeout(() => {
        this.formulaRefocusTimer = null;

        if (!this.formulaBarEditing) {
          return;
        }

        const input = this.$refs.formulaInput;
        input?.focus?.({ preventScroll: true });

        if (typeof input?.setSelectionRange === 'function') {
          const end = String(this.formulaBarDraft || '').length;
          input.setSelectionRange(end, end);
        }
      }, delay);
    },

    referencesMatch(left, right) {
      if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
        return false;
      }

      return left.every((reference, index) => reference === right[index]);
    },

    referencesArePrefixed(prefix, full) {
      if (!Array.isArray(prefix) || !Array.isArray(full) || prefix.length > full.length) {
        return false;
      }

      return prefix.every((reference, index) => reference === full[index]);
    },

    buildFormulaDraftFromReferences(baseDraft, references = []) {
      const base = String(baseDraft ?? '');
      const normalizedReferences = Array.isArray(references) ? references.filter(Boolean) : [];

      if (normalizedReferences.length === 0) {
        return base;
      }

      const separator = this.needsFormulaReferenceSeparator(base) ? ',' : '';
      return `${base}${separator}${normalizedReferences.join(',')}`;
    },

    getSelectedAnchorCell(selection = this.lastSelection) {
      const ranges = Array.isArray(selection?.[0]) ? selection : [];
      const activeRange = ranges.length > 0 ? ranges[ranges.length - 1] : null;

      if (!Array.isArray(activeRange) || activeRange.length < 2) {
        return null;
      }

      const [row, col] = activeRange;
      if (!Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0) {
        return null;
      }

      return { row, col };
    },

    selectionToFormulaReferenceList(selection = this.lastSelection, { excludeTargetCell = false } = {}) {
      const targetCell = excludeTargetCell ? this.cloneFormulaTarget(this.formulaTargetCell) : null;
      const targetReference = targetCell ? `${this.toColumnLabel(targetCell.col)}${targetCell.row + 1}` : '';
      const ranges = Array.isArray(selection?.[0]) ? selection : [];

      return ranges
        .map(([rawRow1, rawCol1, rawRow2, rawCol2]) => {
          if (![rawRow1, rawCol1, rawRow2, rawCol2].every(Number.isInteger)) {
            return '';
          }

          const startRow = Math.max(0, Math.min(rawRow1, rawRow2));
          const endRow = Math.max(0, Math.max(rawRow1, rawRow2));
          const startCol = Math.max(0, Math.min(rawCol1, rawCol2));
          const endCol = Math.max(0, Math.max(rawCol1, rawCol2));
          const startRef = `${this.toColumnLabel(startCol)}${startRow + 1}`;
          const endRef = `${this.toColumnLabel(endCol)}${endRow + 1}`;
          const reference = startRef === endRef ? startRef : `${startRef}:${endRef}`;

          if (targetReference && reference === targetReference) {
            return '';
          }

          return reference;
        })
        .filter(Boolean);
    },

    selectionToFormulaReference(selection = this.lastSelection, options = {}) {
      return this.selectionToFormulaReferenceList(selection, options).join(',');
    },

    getActiveCellInput(cell = this.getSelectedAnchorCell()) {
      if (!cell) {
        return '';
      }

      return this.getInputSheet()?.getCellInput?.(cell.row, cell.col) || '';
    },

    syncFormulaBarFromSelection(preferredCell = null) {
      if (this.formulaBarEditing) {
        return;
      }

      const target = this.cloneFormulaTarget(preferredCell) || this.cloneFormulaTarget(this.getSelectedAnchorCell());
      this.formulaTargetCell = target;
      this.formulaCapturedReferences = [];

      if (!target) {
        this.formulaBarDraft = '';
        this.formulaBarBaseDraft = '';
        return;
      }

      const rawValue = this.getActiveCellInput(target);
      this.formulaBarDraft = rawValue == null ? '' : String(rawValue);
      this.formulaBarBaseDraft = this.formulaBarDraft;
    },

    focusFormulaBar() {
      const target = this.lockFormulaTarget();
      if (!target) {
        return;
      }

      if (!this.formulaBarEditing) {
        this.formulaBarEditing = true;
        this.formulaBarBaseDraft = this.formulaBarDraft;
        this.formulaCapturedReferences = [];
      }

      this.$nextTick(() => {
        const input = this.$refs.formulaInput;
        input?.focus?.();
        if (typeof input?.setSelectionRange === 'function') {
          const end = String(this.formulaBarDraft || '').length;
          input.setSelectionRange(end, end);
        }
      });
    },

    handleFormulaBarFocus() {
      this.clearFormulaRefocusTimer();

      if (!this.formulaTargetCell) {
        this.lockFormulaTarget();
      }

      if (!this.formulaBarEditing) {
        this.formulaBarEditing = true;
        this.formulaBarBaseDraft = this.formulaBarDraft;
        this.formulaCapturedReferences = [];
        return;
      }

      this.formulaBarEditing = true;
    },

    handleFormulaBarInput(event) {
      const nextValue = event?.target?.value ?? '';
      this.formulaBarDraft = nextValue;
      this.formulaBarBaseDraft = nextValue;
      this.formulaCapturedReferences = [];

      if (!this.formulaTargetCell) {
        this.lockFormulaTarget();
      }

      this.formulaBarEditing = true;
    },

    needsFormulaReferenceSeparator(baseDraft) {
      const trimmed = String(baseDraft || '').trimEnd();
      if (!trimmed) {
        return false;
      }

      const lastChar = trimmed.slice(-1);
      return !['=', '+', '-', '*', '/', '^', '(', ',', ':', '<', '>', '&'].includes(lastChar);
    },

    updateFormulaDraftWithSelection(selection = this.lastSelection) {
      if (!this.formulaBarCaptureMode) {
        return;
      }

      const nextReferences = this.selectionToFormulaReferenceList(selection, { excludeTargetCell: true });
      if (nextReferences.length === 0) {
        return;
      }

      const previousReferences = Array.isArray(this.formulaCapturedReferences)
        ? this.formulaCapturedReferences
        : [];

      let capturedReferences = nextReferences;
      if (previousReferences.length > 0 && this.referencesArePrefixed(previousReferences, nextReferences) && nextReferences.length > previousReferences.length) {
        capturedReferences = [
          ...previousReferences,
          ...nextReferences.slice(previousReferences.length)
        ];
      }

      if (this.referencesMatch(previousReferences, capturedReferences)) {
        return;
      }

      this.formulaCapturedReferences = capturedReferences;
      this.formulaBarDraft = this.buildFormulaDraftFromReferences(this.formulaBarBaseDraft, capturedReferences);
      this.queueFormulaInputRefocus();
    },

    startSumFormula() {
      const target = this.lockFormulaTarget();
      if (!target) {
        window.alert('Select the cell that should receive the formula first.');
        return;
      }

      this.formulaBarEditing = true;
      this.formulaBarBaseDraft = '=SUM(';
      this.formulaBarDraft = '=SUM(';
      this.formulaCapturedReferences = [];

      this.$nextTick(() => {
        const input = this.$refs.formulaInput;
        input?.focus?.();
        const end = this.formulaBarDraft.length;
        input?.setSelectionRange?.(end, end);
      });
    },

    normalizeFormulaBarValue(value) {
      const rawValue = String(value ?? '');
      const trimmed = rawValue.trim();

      if (!trimmed.startsWith('=')) {
        return rawValue;
      }

      const lastChar = trimmed.slice(-1);
      if (!lastChar || ['(', ',', '+', '-', '*', '/', '^', '=', '<', '>', '&'].includes(lastChar)) {
        return rawValue;
      }

      const openCount = (rawValue.match(/\(/g) || []).length;
      const closeCount = (rawValue.match(/\)/g) || []).length;
      if (closeCount >= openCount) {
        return rawValue;
      }

      return `${rawValue}${')'.repeat(openCount - closeCount)}`;
    },

    applyFormulaBar() {
      this.clearFormulaRefocusTimer();

      const target = this.lockFormulaTarget();
      if (!target) {
        window.alert('Select the cell that should receive the value first.');
        return;
      }

      const valueToApply = this.normalizeFormulaBarValue(this.formulaBarDraft);
      const applied = this.getInputSheet()?.setCellInput?.(target.row, target.col, valueToApply, 'formula_bar');
      if (!applied) {
        return;
      }

      this.formulaBarEditing = false;
      this.formulaCapturedReferences = [];
      this.formulaTargetCell = this.cloneFormulaTarget(target);
      this.formulaBarDraft = valueToApply == null ? '' : String(valueToApply);
      this.formulaBarBaseDraft = this.formulaBarDraft;
      this.getInputSheet()?.selectCell?.(target.row, target.col);

      this.$nextTick(() => {
        this.syncFormulaBarFromSelection(target);
      });
    },

    cancelFormulaBarEdit() {
      this.clearFormulaRefocusTimer();

      const target = this.lockFormulaTarget();
      this.formulaBarEditing = false;
      this.formulaCapturedReferences = [];

      if (target) {
        this.getInputSheet()?.selectCell?.(target.row, target.col);
        this.$nextTick(() => {
          this.syncFormulaBarFromSelection(target);
        });
        return;
      }

      this.syncFormulaBarFromSelection();
    },

    cloneSelectionRanges(selection = this.lastSelection) {
      if (!Array.isArray(selection) || selection.length === 0 || !Array.isArray(selection[0])) {
        return null;
      }

      return selection
        .filter(range => Array.isArray(range) && range.length >= 4)
        .map(range => range.slice(0, 4).map(value => Number(value)));
    },

    hasSelectionRanges(selection = this.lastSelection) {
      return Array.isArray(selection) && selection.length > 0 && Array.isArray(selection[0]);
    },

    isColorPickerSelectionLocked() {
      return !!this.activeColorPicker && this.hasSelectionRanges(this.colorPickerSelectionSnapshot);
    },

    clearColorPickerCloseTimer() {
      if (this.colorPickerCloseTimer) {
        window.clearTimeout(this.colorPickerCloseTimer);
        this.colorPickerCloseTimer = null;
      }
    },

    restoreInputSelection(selection = this.lastSelection) {
      const ranges = this.cloneSelectionRanges(selection);
      const hot = this.getInputHot();

      if (!ranges || !hot) {
        return false;
      }

      try {
        if (typeof hot.selectCells === 'function') {
          hot.selectCells(ranges, false, false);
        } else {
          const [row, col] = ranges[0];
          hot.selectCell?.(row, col);
        }

        this.lastSelection = ranges;
        return true;
      } catch (error) {
        console.warn('Failed to restore spreadsheet selection after color pick:', error);
        return false;
      }
    },

    beginColorPickerSession(kind) {
      const selection = this.cloneSelectionRanges(this.lastSelection);
      if (!selection) {
        window.alert('Select one or more cells before choosing a color.');
        return false;
      }

      this.clearColorPickerCloseTimer();
      this.activeColorPicker = kind;
      this.colorPickerSelectionSnapshot = selection;
      this.lastSelection = selection;

      this.$nextTick(() => {
        this.restoreInputSelection(selection);
      });

      return true;
    },

    finishColorPickerSession({ restoreSelection = true } = {}) {
      const selection = this.cloneSelectionRanges(this.colorPickerSelectionSnapshot);

      this.clearColorPickerCloseTimer();
      this.activeColorPicker = '';
      this.colorPickerSelectionSnapshot = null;

      if (restoreSelection && selection) {
        this.$nextTick(() => {
          this.restoreInputSelection(selection);
        });
      }
    },

    handleColorPickerBlur(kind) {
      if (this.activeColorPicker !== kind) {
        return;
      }

      this.clearColorPickerCloseTimer();
      this.colorPickerCloseTimer = window.setTimeout(() => {
        if (this.activeColorPicker === kind) {
          this.finishColorPickerSession({ restoreSelection: true });
        }
      }, 40);
    },

    handleInputSelectionChange(selection) {
      if (this.isColorPickerSelectionLocked()) {
        return;
      }

      this.lastSelection = selection;

      if (this.formulaBarEditing) {
        if (this.formulaBarCaptureMode) {
          this.updateFormulaDraftWithSelection(selection);
        }
        return;
      }

      this.syncFormulaBarFromSelection();
    },

    handleInputContextMenuCell(payload = {}) {
      this.inputContextMenuPayload = payload && typeof payload === 'object' ? { ...payload } : null;
      this.pathOperationContextMenuSelection = Array.isArray(payload?.selection?.[0])
        ? payload.selection.map(range => range.slice(0, 4))
        : null;
      if (this.pathOperationContextMenuSelection) {
        this.lastSelection = this.pathOperationContextMenuSelection.map(range => range.slice(0, 4));
      }
      this.pathOperationContextMenuCell = payload?.cell && typeof payload.cell === 'object'
        ? { ...payload.cell }
        : null;

      this.pathOperationContextMenuCandidate = null;
      const state = this.getCaseBuddyContextMenuPathOperationState('prune');
      this.pathOperationContextMenuCandidate = state.visible ? state.candidate || null : null;
    },

    getRowsFromContextMenuPayload(payload = this.inputContextMenuPayload) {
      const rows = new Set();
      const selection = Array.isArray(payload?.selection?.[0]) ? payload.selection : [];

      selection.forEach(([rawStartRow, , rawEndRow]) => {
        if (!Number.isInteger(rawStartRow) || !Number.isInteger(rawEndRow)) return;
        const startRow = Math.max(0, Math.min(rawStartRow, rawEndRow));
        const endRow = Math.max(0, Math.max(rawStartRow, rawEndRow));
        for (let row = startRow; row <= endRow; row += 1) rows.add(row);
      });

      if (rows.size === 0 && Number.isInteger(payload?.cell?.row) && payload.cell.row >= 0) {
        rows.add(payload.cell.row);
      }

      return [...rows].sort((left, right) => left - right);
    },

    getCaseBuddyContextMenuPathOperationState(action = 'prune', payload = this.inputContextMenuPayload) {
      const contextPayload = payload && typeof payload === 'object' ? payload : {};
      const mainSheet = this.findLogicalSheetMeta('main');
      const sheetId = contextPayload.sheetId || '';
      const actionKey = ['prune', 'graft', 'bridge'].includes(action) ? action : 'prune';

      if (!mainSheet?.id || sheetId !== mainSheet.id) {
        return { visible: false, enabled: false, actions: {} };
      }

      const rows = this.getRowsFromContextMenuPayload(contextPayload);
      if (rows.length !== 1 || rows[0] <= 0) {
        return { visible: false, enabled: false, actions: {} };
      }

      const clickedRow = Number.isInteger(contextPayload?.cell?.row) ? contextPayload.cell.row : rows[0];
      if (clickedRow !== rows[0]) {
        return { visible: false, enabled: false, actions: {} };
      }

      let candidate = this.pathOperationContextMenuCandidate || null;
      if (!candidate || candidate.rowIndex !== rows[0]) {
        const analysis = buildPathOperationAnalysis(this.getMatrixForLogicalSheet('main'), this.getMatrixForLogicalSheet('raw'));
        candidate = findExactPathOperationCandidateForRow(analysis, rows[0]);
      }

      if (!candidate) {
        return { visible: false, enabled: false, actions: {} };
      }

      const baseRowLabel = `row ${candidate.spreadsheetRowNumber}`;
      return {
        visible: true,
        enabled: true,
        candidate,
        candidateId: candidate.id,
        rowIndex: candidate.rowIndex,
        spreadsheetRowNumber: candidate.spreadsheetRowNumber,
        action: actionKey,
        actions: {
          prune: {
            enabled: !!candidate.canPrune,
            label: `🌿 Case Buddy: Prune ${baseRowLabel}`
          },
          graft: {
            enabled: !!candidate.canGraft,
            label: `🌱 Case Buddy: Graft from ${baseRowLabel}`
          },
          bridge: {
            enabled: !!candidate.canBridge,
            label: `🌉 Case Buddy: Bridge graft from ${baseRowLabel}`
          }
        }
      };
    },

    isCaseBuddyContextMenuActionVisible(action = 'prune', payload = this.inputContextMenuPayload) {
      return !!this.getCaseBuddyContextMenuPathOperationState(action, payload).visible;
    },

    isCaseBuddyContextMenuActionEnabled(action = 'prune', payload = this.inputContextMenuPayload) {
      const state = this.getCaseBuddyContextMenuPathOperationState(action, payload);
      return !!state.visible && state.enabled !== false && state.actions?.[action]?.enabled !== false;
    },

    getCaseBuddyContextMenuActionLabel(action = 'prune', payload = this.inputContextMenuPayload) {
      const state = this.getCaseBuddyContextMenuPathOperationState(action, payload);
      return state.actions?.[action]?.label || 'Case Buddy: Prune / Graft / Bridge';
    },

    handleInputContextMenuPathOperation(action = 'prune', payload = this.inputContextMenuPayload) {
      const state = this.getCaseBuddyContextMenuPathOperationState(action, payload);
      if (!state.visible || !state.candidateId) {
        this.showCaseToast('Select one qualified Main Data output row with funds followed and left-bounds data first.');
        return;
      }

      if (state.actions?.[action]?.enabled === false) {
        this.showCaseToast('That path operation is not available for the selected row. Graft and Bridge require a current Path ends row.');
        return;
      }

      this.openPathOperationModal({ candidateId: state.candidateId, action, source: 'context-menu' });
    },

    updateAddressColorCheckResult(analysis = null) {
      const mainSheet = this.findLogicalSheetMeta('main');
      const mainSheetState = mainSheet?.id
        ? this.getSheetStateById(mainSheet.id)
        : createCaseSheetState({ includeHeaders: true });
      const effectiveAnalysis = analysis || buildCaseAddressColorCoverageAnalysis(
        mainSheetState,
        this.caseSupportState.workflowState || {},
        this.caseSupportState.caseDataRevision
      );

      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        address_colors: this.buildCheckResult(
          'address_colors',
          effectiveAnalysis.addressCellCount === 0 ? 'warn' : effectiveAnalysis.complete ? 'pass' : 'fail',
          effectiveAnalysis.addressCellCount === 0
            ? 'No From/To address cells were detected in Main Data Column C/D yet.'
            : effectiveAnalysis.complete
              ? `Address coloring is current for revision ${this.caseSupportState.caseDataRevision}. ${effectiveAnalysis.coloredAddressCellCount}/${effectiveAnalysis.addressCellCount} From/To address cell${effectiveAnalysis.addressCellCount === 1 ? '' : 's'} are highlighted across ${effectiveAnalysis.uniqueAddressCount} unique address${effectiveAnalysis.uniqueAddressCount === 1 ? '' : 'es'}.`
              : effectiveAnalysis.run
                ? 'Main Data has changed or address colors were cleared since the last C/D address coloring run. Re-run Color C/D Addresses or mark the workflow step manually.'
                : 'C/D address coloring has not been run for this case yet.',
          effectiveAnalysis
        )
      };
      this.persistCaseSupportState();
      return this.caseSupportState.checkResultsById.address_colors;
    },

    applyCaseAddressColors() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const workbook = this.$refs.inputSpreadsheet;
      const mainSheet = this.findLogicalSheetMeta('main');

      if (!workbook || !mainSheet?.id) {
        this.addressColoringStatus = 'Main Data sheet is not available yet.';
        this.showCaseToast(this.addressColoringStatus);
        return null;
      }

      const previousActiveSheetId = workbook.activeSheetId;
      const currentState = this.getSheetStateById(mainSheet.id);
      const seed = Math.floor(Math.random() * 360);
      const result = buildCaseAddressColorState(currentState, { seed });

      if (result.addressCellCount === 0) {
        this.addressColoringStatus = 'No From/To address cells were found in Main Data Columns C and D.';
        this.updateAddressColorCheckResult(buildCaseAddressColorCoverageAnalysis(
          currentState,
          this.caseSupportState.workflowState || {},
          this.caseSupportState.caseDataRevision
        ));
        this.showCaseToast(this.addressColoringStatus);
        return result;
      }

      this.writeSheetStateById(mainSheet.id, result.state);

      const revision = Number(this.caseSupportState.caseDataRevision) || 0;
      this.caseSupportState.workflowState.addressColorRun = {
        appliedAtUtc: new Date().toISOString(),
        revision,
        seed,
        addressCellCount: result.addressCellCount,
        uniqueAddressCount: result.uniqueAddressCount,
        repeatedAddressCount: result.repeatedAddressCount,
        repeatedAddressCellCount: result.repeatedAddressCellCount,
        coloredCellCount: result.coloredCellCount
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_ADDRESS_FLOW_COLORS_APPLIED, CASE_ADDRESS_COLOR_TOOL_RUN_ID, { silent: true });

      const analysis = buildCaseAddressColorCoverageAnalysis(
        result.state,
        this.caseSupportState.workflowState || {},
        revision
      );
      this.updateAddressColorCheckResult(analysis);
      this.recordWorkflowToolRun(CASE_ADDRESS_COLOR_TOOL_RUN_ID, {
        revision,
        seed,
        addressCellCount: result.addressCellCount,
        uniqueAddressCount: result.uniqueAddressCount,
        repeatedAddressCount: result.repeatedAddressCount,
        coloredCellCount: result.coloredCellCount
      });

      this.addressColoringLastRunSummary = {
        ...this.caseSupportState.workflowState.addressColorRun,
        completedAtUtc: this.caseSupportState.workflowState.addressColorRun.appliedAtUtc
      };
      this.addressColoringStatus = `Colored ${result.coloredCellCount} C/D address cell${result.coloredCellCount === 1 ? '' : 's'} across ${result.uniqueAddressCount} unique address value${result.uniqueAddressCount === 1 ? '' : 's'}. ${result.repeatedAddressCount} repeated group${result.repeatedAddressCount === 1 ? '' : 's'} will share matching colors.`;

      if (previousActiveSheetId !== mainSheet.id) {
        workbook.activateSheet?.(mainSheet.id);
      } else {
        workbook.getActiveSheet?.()?.restoreFormatting?.();
      }

      this.queueCaseSummaryRefresh(120);
      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
      }
      this.showCaseToast(this.addressColoringStatus);
      return result;
    },

    clearCaseAddressColors() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const workbook = this.$refs.inputSpreadsheet;
      const mainSheet = this.findLogicalSheetMeta('main');

      if (!workbook || !mainSheet?.id) {
        this.addressColoringStatus = 'Main Data sheet is not available yet.';
        this.showCaseToast(this.addressColoringStatus);
        return null;
      }

      const previousActiveSheetId = workbook.activeSheetId;
      const currentState = this.getSheetStateById(mainSheet.id);
      const result = clearCaseAddressColorState(currentState);
      this.writeSheetStateById(mainSheet.id, result.state);

      const clearedRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      this.caseSupportState.workflowState.addressColorRun = {
        clearedAtUtc: new Date().toISOString(),
        revision: null,
        clearedRevision,
        clearedCellCount: result.clearedCellCount,
        state: 'cleared'
      };
      const signals = { ...(this.caseSupportState.workflowState.signalCompleteByCode || {}) };
      delete signals[WORKFLOW_SIGNAL_ADDRESS_FLOW_COLORS_APPLIED];
      this.caseSupportState.workflowState.signalCompleteByCode = signals;

      const analysis = buildCaseAddressColorCoverageAnalysis(
        result.state,
        this.caseSupportState.workflowState || {},
        clearedRevision
      );
      this.updateAddressColorCheckResult(analysis);
      this.recordWorkflowToolRun(CASE_ADDRESS_COLOR_CLEAR_TOOL_RUN_ID, {
        clearedCellCount: result.clearedCellCount,
        revision: clearedRevision
      });

      this.addressColoringLastRunSummary = {
        clearedAtUtc: this.caseSupportState.workflowState.addressColorRun.clearedAtUtc,
        clearedCellCount: result.clearedCellCount
      };
      this.addressColoringStatus = result.clearedCellCount > 0
        ? `Cleared Case Buddy address colors from ${result.clearedCellCount} cell${result.clearedCellCount === 1 ? '' : 's'}.`
        : 'No Case Buddy address colors were present to clear.';

      if (previousActiveSheetId !== mainSheet.id) {
        workbook.activateSheet?.(mainSheet.id);
      } else {
        workbook.getActiveSheet?.()?.restoreFormatting?.();
      }

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
      }
      this.showCaseToast(this.addressColoringStatus);
      return result;
    },

    syncGridStateFromHot() {
      const hot = this.$refs.inputSpreadsheet?.hotInstance;
      if (!hot) return;

      this.freezeTop = hot.getSettings().fixedRowsTop > 0;
      this.freezeLeft = hot.getSettings().fixedColumnsLeft > 0 || hot.getSettings().fixedColumnsStart > 0;
    },

    getInputSheet() {
      return this.$refs.inputSpreadsheet || null;
    },

    getInputHot() {
      return this.getInputSheet()?.hotInstance || null;
    },

    getInputUndoRedoPlugin() {
      const sheet = this.getInputSheet();
      const hot = this.getInputHot();
      return sheet?.getUndoRedoPlugin?.() || hot?.getPlugin?.('undoRedo') || hot?.undoRedo || null;
    },

    isTextEditingTarget(target) {
      if (!(target instanceof HTMLElement)) {
        return false;
      }

      const tagName = target.tagName;
      return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
    },

    isInsideOutputSheet(element) {
      return element instanceof Element && !!element.closest('.sheet-panel--output');
    },

    shouldProxyInputShortcut(target) {
      const activeElement = document.activeElement;

      if (this.isInsideOutputSheet(target) || this.isInsideOutputSheet(activeElement)) {
        return false;
      }

      return !(activeElement instanceof Element && activeElement.closest('.sheet-grid'));
    },

    undoInputSheet() {
      const plugin = this.getInputUndoRedoPlugin();

      if (!plugin) {
        return;
      }

      if (typeof plugin.isUndoAvailable === 'function' && !plugin.isUndoAvailable()) {
        return;
      }

      plugin.undo?.();
    },

    redoInputSheet() {
      const plugin = this.getInputUndoRedoPlugin();

      if (!plugin) {
        return;
      }

      if (typeof plugin.isRedoAvailable === 'function' && !plugin.isRedoAvailable()) {
        return;
      }

      plugin.redo?.();
    },

    runUndoableClassChange(label, mutation) {
      const sheet = this.getInputSheet();

      if (sheet?.runUndoableClassChange) {
        return sheet.runUndoableClassChange(label, this.lastSelection, mutation);
      }

      if (typeof mutation === 'function') {
        mutation();
      }

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }

      return true;
    },

    runUndoableFullClassChange(label, mutation) {
      const sheet = this.getInputSheet();

      if (sheet?.runUndoableFullClassChange) {
        return sheet.runUndoableFullClassChange(label, mutation);
      }

      if (typeof mutation === 'function') {
        mutation();
      }

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }

      return true;
    },

    runUndoableBorderChange(label, mutation) {
      const sheet = this.getInputSheet();

      if (sheet?.runUndoableBorderChange) {
        return sheet.runUndoableBorderChange(label, this.lastSelection, mutation);
      }

      if (typeof mutation === 'function') {
        mutation();
      }

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }

      return true;
    },

    closeShowcaseMenu() {
      this.$refs.showcaseMenu?.removeAttribute('open');
    },

    runShowcaseAction(action) {
      this.closeShowcaseMenu();

      const actionMap = {
        mock: () => this.addMockData(),
        errors: () => this.highlightColumn2(),
        zebra: () => this.highlightAlternatingRows(),
        keywords: () => this.highlightKeywordRows(),
        analyze: () => this.analyzeColumnTypes()
      };

      actionMap[action]?.();
    },

    setInputFullscreen(value) {
      this.inputFullscreen = value;
      document.body.style.overflow = value ? 'hidden' : '';

      this.$nextTick(() => {
        this.$refs.inputSpreadsheet?.refreshDimensions?.();
      });
    },

    onAutoSaveToggle() {
      this.safeCaseBuddyLocalStorageSet?.('tool60_autosave_enabled', JSON.stringify(this.autoSaveEnabled), { silent: true }) || localStorage.setItem('tool60_autosave_enabled', JSON.stringify(this.autoSaveEnabled));

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile();
      }
    },

    setAlignment(alignment) {
      this.runUndoableClassChange(`align_${alignment}`, () => {
        setSelectionAlignment(this.getInputSheet(), this.lastSelection, alignment, null);
      });
    },

    setVertical(alignment) {
      this.runUndoableClassChange(`valign_${alignment}`, () => {
        setSelectionVerticalAlignment(this.getInputSheet(), this.lastSelection, alignment, null);
      });
    },

    changeFontSize(delta) {
      this.currentFontSize = Math.max(8, Math.min(72, this.currentFontSize + delta));
      this.applyFontSize();
    },

    applyFontSize() {
      this.runUndoableClassChange(`font_${this.currentFontSize}`, () => {
        setSelectionFontSize(this.getInputSheet(), this.lastSelection, this.currentFontSize, null);
      });
    },

    toggleBold() {
      this.runUndoableClassChange('toggle_bold', () => {
        toggleSelectionCellStyle(this.getInputSheet(), 'bold-cell');
      });
    },

    toggleItalic() {
      this.runUndoableClassChange('toggle_italic', () => {
        toggleSelectionCellStyle(this.getInputSheet(), 'italic-cell');
      });
    },

    toggleUnderline() {
      this.runUndoableClassChange('toggle_underline', () => {
        toggleSelectionCellStyle(this.getInputSheet(), 'underline-cell');
      });
    },

    toggleStrikeThrough() {
      this.runUndoableClassChange('toggle_strike', () => {
        toggleSelectionCellStyle(this.getInputSheet(), 'strike-cell');
      });
    },

    toggleWrap() {
      this.runUndoableClassChange('toggle_wrap', () => {
        toggleSelectionWrap(this.getInputSheet(), this.lastSelection, null);
      });
    },

    toggleColorPicker(kind) {
      if (this.activeColorPicker === kind) {
        this.cancelColorPicker();
        return;
      }

      if (!this.beginColorPickerSession(kind)) {
        return;
      }

      this.$nextTick(() => {
        const inputRef = kind === 'text' ? this.$refs.textColorInput : this.$refs.fillColorInput;
        inputRef?.focus?.({ preventScroll: true });
      });
    },

    openColorPicker() {
      this.toggleColorPicker('fill');
    },

    openTextColorPicker() {
      this.toggleColorPicker('text');
    },

    confirmColorPicker() {
      const kind = this.activeColorPicker;
      const color = kind === 'text' ? this.textColorValue : this.fillColorValue;
      const lockedSelection = this.cloneSelectionRanges(this.colorPickerSelectionSnapshot) || this.cloneSelectionRanges(this.lastSelection);

      if (!kind || !lockedSelection || !color) {
        this.finishColorPickerSession({ restoreSelection: false });
        return;
      }

      this.lastSelection = lockedSelection;

      if (kind === 'text') {
        this.runUndoableClassChange(`text_${color}`, () => {
          applyTextColorToSelection(this.getInputSheet(), lockedSelection, color);
        });
      } else {
        this.runUndoableClassChange(`fill_${color}`, () => {
          applyBackgroundColorToSelection(this.getInputSheet(), lockedSelection, color);
        });
      }

      this.finishColorPickerSession({ restoreSelection: true });
    },

    cancelColorPicker() {
      if (!this.activeColorPicker) {
        return;
      }

      this.finishColorPickerSession({ restoreSelection: true });
    },

    applyBackgroundColor(event) {
      this.fillColorValue = event?.target?.value || this.fillColorValue;
      this.activeColorPicker = 'fill';
      this.confirmColorPicker();
    },

    applyTextColor(event) {
      this.textColorValue = event?.target?.value || this.textColorValue;
      this.activeColorPicker = 'text';
      this.confirmColorPicker();
    },

    applyBorderStyle(type) {
      this.runUndoableBorderChange(`border_${type}`, () => {
        applyBorderStyleToSelection(this.getInputSheet(), this.lastSelection, type, null);
      });

      this.showBorderModal = false;
    },

    fillDownSelection() {
      const hot = this.getInputHot();
      if (!hot) return;

      fillDownPattern(hot);
    },

    applySearch() {
      const hot = this.$refs.inputSpreadsheet?.hotInstance;
      const query = this.searchQuery.trim();

      if (!hot) return;
      if (!query) {
        this.clearSearch();
        return;
      }

      const searchPlugin = hot.getPlugin('search');
      const results = searchPlugin.query(query);

      hot.render();
      this.matchCount = results.length;

      if (results.length > 0) {
        const firstMatch = results[0];
        hot.scrollViewportTo(firstMatch.row, firstMatch.col, true, true);
        hot.selectCell(firstMatch.row, firstMatch.col);
      } else {
        alert('No results found.');
      }
    },

    clearSearch() {
      const hot = this.$refs.inputSpreadsheet?.hotInstance;
      if (!hot) return;

      clearSheetSearch(hot);
      this.searchQuery = '';
      this.matchCount = 0;
    },

    toggleFreezeTopRow() {
      const hot = this.$refs.inputSpreadsheet?.hotInstance;
      if (!hot) return;

      this.freezeTop = !this.freezeTop;
      hot.updateSettings({ fixedRowsTop: this.freezeTop ? 1 : 0 });
      hot.render();

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }
    },

    toggleFreezeFirstColumn() {
      const hot = this.$refs.inputSpreadsheet?.hotInstance;
      if (!hot) return;

      this.freezeLeft = !this.freezeLeft;
      hot.updateSettings({ fixedColumnsLeft: this.freezeLeft ? 1 : 0 });
      hot.render();

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }
    },

    resetActiveSheetRowHeights() {
      const workbook = this.$refs.inputSpreadsheet;
      const activeSheetName = workbook?.activeSheetName || 'the active sheet';
      const result = workbook?.resetRowHeightsToDefault?.()
        ?? workbook?.resetRowDisplayToDefault?.()
        ?? { ok: false, status: 'Row-height reset is unavailable for the active spreadsheet.' };

      this.showCaseToast(result?.status || `Reset row heights for ${activeSheetName}.`);

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }

      return result;
    },

    resetFormatting() {
      const activeSheetName = this.$refs.inputSpreadsheet?.activeSheetName || 'the active sheet';
      const confirmed = window.confirm(
        `Clear "${activeSheetName}" data and reset formatting, borders, frozen panes, and search?`
      );

      if (!confirmed) {
        return;
      }

      this.closeShowcaseMenu();
      this.$refs.inputSpreadsheet?.resetSheet?.();
      this.showBorderModal = false;
      this.showPathOperationModal = false;
      this.pathOperationRunning = false;
      this.searchQuery = '';
      this.matchCount = 0;
      this.freezeTop = false;
      this.freezeLeft = false;
      this.lastSelection = null;
      this.markCaseDataMutated('active_sheet_reset', { activeSheetName });

      if (this.autoSaveEnabled) {
        this.saveSpreadsheetFromEvent();
      }
    },

requestResetSheet() {
  this.showResetConfirm = true;
},

cancelResetSheet() {
  this.showResetConfirm = false;
},

async performResetSheetReset() {
  const workbook = this.$refs.inputSpreadsheet;
  const activeSheetName = workbook?.activeSheetName || 'the active sheet';

  this.showResetConfirm = false;
  this.closeShowcaseMenu();

  const resetResult = workbook?.resetSheet?.()
    ?? { ok: false, status: 'Input workbook unavailable.' };

  this.showBorderModal = false;
  this.resetCaseInvestigationVisualizerState?.({ close: true, reason: 'sheet-reset' });
  this.searchQuery = '';
  this.matchCount = 0;
  this.freezeTop = false;
  this.freezeLeft = false;
  this.lastSelection = null;

  this.formulaBarEditing = false;
  this.formulaTargetCell = null;
  this.formulaCapturedReferences = [];
  this.formulaBarDraft = '';
  this.formulaBarBaseDraft = '';

  this.agentCellValue = '';

  await this.$nextTick();
  await new Promise(resolve => requestAnimationFrame(resolve));

  const sheet = this.getInputSheet();
  const verificationRange = 'A1:C3';
  const verificationValues = sheet?.getRangeData?.(0, 0, 2, 2) ?? [];

  const allEmpty = verificationValues.every(row =>
    Array.isArray(row) && row.every(value => value === '' || value == null)
  );
this.agentResetCount += 1;
this.agentLastResetAt = new Date().toISOString();
  const payload = {
    ok: !!resetResult.ok && allEmpty,
    status: allEmpty
      ? `Reset ${activeSheetName} and verified ${verificationRange} is empty.`
      : `Reset ${activeSheetName} was attempted, but ${verificationRange} still contains data.`,
    sheet: activeSheetName,
    verificationRange,
    values: verificationValues,
    resetCount: this.agentResetCount,
lastResetAt: this.agentLastResetAt
  };

  this.agentStatus = payload.status;
  this.agentReadback = JSON.stringify(payload, null, 2);

  this.markCaseDataMutated('active_sheet_reset', { activeSheetName });

  if (this.autoSaveEnabled) {
    this.saveSpreadsheetFromEvent();
  }

  return payload;
},

    analyzeColumnTypes() {
      const hot = this.$refs.inputSpreadsheet?.hotInstance;
      if (!hot) return;

      const types = detectColumnTypes(hot);
      const lines = types.map((type, index) => `${this.toColumnLabel(index)}: ${type}`);
      alert(`Column types:\n${lines.join('\n')}`);
    },

    highlightKeywordRows() {
      this.runUndoableFullClassChange('highlight_keywords', () => {
        highlightKeywordRowsInSheet(this.getInputSheet(), null);
      });
    },

    highlightAlternatingRows() {
      this.runUndoableFullClassChange('highlight_zebra', () => {
        zebraStripeRows(this.getInputSheet(), null);
      });
    },

    addMockData() {
      loadMockData(this.$refs.inputSpreadsheet);
      this.markCaseDataMutated('mock_data_loaded');
      if (this.autoSaveEnabled) this.saveSpreadsheetFromEvent();
    },

    highlightColumn2() {
      this.runUndoableFullClassChange('highlight_errors', () => {
        highlightErrorColumn(this.getInputSheet(), null);
      });
    },

    handleDeselection() {
      if (this.isColorPickerSelectionLocked()) {
        return;
      }

      this.lastSelection = null;
      if (!this.formulaBarEditing) {
        this.syncFormulaBarFromSelection();
      }
    },

    handleActiveInputSheetChange() {
      this.clearFormulaRefocusTimer();
      this.finishColorPickerSession({ restoreSelection: false });
      this.searchQuery = '';
      this.matchCount = 0;
      this.showBorderModal = false;
      this.formulaBarEditing = false;
      this.formulaTargetCell = null;
      this.formulaCapturedReferences = [];
      this.formulaBarDraft = '';
      this.formulaBarBaseDraft = '';
      this.syncGridStateFromHot();
      this.registerCaseMutationWatchers(this.getInputHot(), 'input-workbook');
      this.queueCaseSummaryRefresh(180);

      this.$nextTick(() => {
        this.$refs.inputSpreadsheet?.refreshDimensions?.();
        this.syncFormulaBarFromSelection();
      });
    },

    normalizeSpreadsheetCopyValue(value) {
      if (value == null) return '';
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString();
      }
      return String(value);
    },

    cellHasCopyValue(value) {
      return this.normalizeSpreadsheetCopyValue(value).trim() !== '';
    },

    buildTrimmedSpreadsheetCopyMatrix(matrix = []) {
      const rows = Array.isArray(matrix) ? matrix : [];
      let lastRowIndex = -1;
      let lastColIndex = -1;
      let lastBodyColIndex = -1;

      rows.forEach((row, rowIndex) => {
        const cells = Array.isArray(row) ? row : [];
        cells.forEach((cell, colIndex) => {
          if (this.cellHasCopyValue(cell)) {
            lastRowIndex = Math.max(lastRowIndex, rowIndex);
            lastColIndex = Math.max(lastColIndex, colIndex);
            if (rowIndex > 0) {
              lastBodyColIndex = Math.max(lastBodyColIndex, colIndex);
            }
          }
        });
      });

      if (lastRowIndex < 0 || lastColIndex < 0) {
        return [];
      }

      const copyLastColIndex = lastBodyColIndex >= 0 ? lastBodyColIndex : lastColIndex;

      return rows.slice(0, lastRowIndex + 1).map((row) => {
        const cells = Array.isArray(row) ? row : [];
        return Array.from({ length: copyLastColIndex + 1 }, (_, colIndex) => this.normalizeSpreadsheetCopyValue(cells[colIndex]));
      });
    },

    escapeSpreadsheetCopyTsvCell(value) {
      const text = this.normalizeSpreadsheetCopyValue(value);
      if (!/[\t\r\n"]/.test(text)) {
        return text;
      }
      return `"${text.replace(/"/g, '""')}"`;
    },

    buildSpreadsheetCopyTsv(matrix = []) {
      return (Array.isArray(matrix) ? matrix : [])
        .map(row => (Array.isArray(row) ? row : []).map(value => this.escapeSpreadsheetCopyTsvCell(value)).join('\t'))
        .join('\n');
    },

    clearSpreadsheetCopyTimers() {
      ['input', 'output'].forEach((target) => {
        if (this.spreadsheetCopyTimers?.[target]) {
          window.clearTimeout(this.spreadsheetCopyTimers[target]);
          this.spreadsheetCopyTimers[target] = null;
        }
      });
    },

    setSpreadsheetCopyFeedback(target, message, { busy = false, clearDelay = 3600 } = {}) {
      if (!['input', 'output'].includes(target)) {
        return;
      }

      if (this.spreadsheetCopyTimers?.[target]) {
        window.clearTimeout(this.spreadsheetCopyTimers[target]);
        this.spreadsheetCopyTimers[target] = null;
      }

      this.spreadsheetCopyBusy = {
        ...this.spreadsheetCopyBusy,
        [target]: !!busy
      };
      this.spreadsheetCopyStatus = {
        ...this.spreadsheetCopyStatus,
        [target]: String(message || '').trim()
      };

      if (message && clearDelay > 0) {
        this.spreadsheetCopyTimers[target] = window.setTimeout(() => {
          this.spreadsheetCopyTimers[target] = null;
          this.spreadsheetCopyStatus = {
            ...this.spreadsheetCopyStatus,
            [target]: ''
          };
        }, clearDelay);
      }
    },

    async copySpreadsheetMatrixToClipboard(matrix = [], options = {}) {
      const target = options.target === 'output' ? 'output' : 'input';
      const sheetName = String(options.sheetName || (target === 'output' ? 'Output Spreadsheet' : 'Input Spreadsheet')).trim();
      this.setSpreadsheetCopyFeedback(target, 'Preparing copy…', { busy: true, clearDelay: 0 });

      try {
        await this.$nextTick();
        const trimmed = this.buildTrimmedSpreadsheetCopyMatrix(matrix);
        if (trimmed.length === 0) {
          const emptyMessage = `${sheetName} has no data to copy.`;
          this.setSpreadsheetCopyFeedback(target, emptyMessage, { busy: false, clearDelay: 4200 });
          this.showCaseToast(emptyMessage);
          return false;
        }

        const tsv = this.buildSpreadsheetCopyTsv(trimmed);
        const copied = await this.copyTextToClipboard(tsv, `${sheetName} copied.`);
        const rowCount = trimmed.length;
        const colCount = Math.max(0, ...trimmed.map(row => Array.isArray(row) ? row.length : 0));
        const message = copied
          ? `Copied ${rowCount} row${rowCount === 1 ? '' : 's'} × ${colCount} column${colCount === 1 ? '' : 's'}.`
          : 'Could not copy in this browser.';

        this.setSpreadsheetCopyFeedback(target, message, { busy: false, clearDelay: copied ? 4200 : 5200 });
        if (!copied) {
          this.showCaseToast(message);
        }
        return copied;
      } catch (error) {
        console.warn('Failed to copy spreadsheet contents.', error);
        const message = 'Could not copy this spreadsheet.';
        this.setSpreadsheetCopyFeedback(target, message, { busy: false, clearDelay: 5200 });
        this.showCaseToast(message);
        return false;
      }
    },

    async copyActiveInputSheetToClipboard() {
      const workbook = this.$refs.inputSpreadsheet;
      const matrix = workbook?.getData?.() || [];
      const sheetName = workbook?.activeSheetName || this.findLogicalSheetMeta?.('main')?.name || 'Input Spreadsheet';
      return this.copySpreadsheetMatrixToClipboard(matrix, { target: 'input', sheetName });
    },

    async copyOutputSheetToClipboard() {
      const matrix = this.$refs.outputSpreadsheet?.getData?.() || this.getOutputMatrix?.() || [];
      return this.copySpreadsheetMatrixToClipboard(matrix, { target: 'output', sheetName: 'Output Spreadsheet' });
    },

    processData() {
      processSpreadsheetData(this.$refs.inputSpreadsheet, this.$refs.outputSpreadsheet);
      this.markOutputBufferGenerated({
        title: 'Processed spreadsheet output',
        detail: 'Showcase processing output is staged for review.',
        sourceActionId: 'process_data',
        target: 'output'
      });
      if (this.autoSaveEnabled) {
        this.handleOutputSheetSaveRequest();
      }
    },

    clearOutput() {
      clearSheetOutput(this.$refs.outputSpreadsheet);
      this.setOutputReviewState({
        status: 'empty',
        title: '',
        detail: '',
        generatedAtUtc: '',
        sourceActionId: '',
        sourceDeliverableId: '',
        target: '',
        reviewedAtUtc: ''
      });
      if (this.autoSaveEnabled) {
        this.handleOutputSheetSaveRequest();
      }
    },

    exportToCSV() {
      exportSheetToCSV(this.$refs.outputSpreadsheet);
    }
  
};
